import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { applyNodeChanges, type NodeChange } from "@xyflow/react";
import { useGetSalesOrderInit } from "../../hooks/SalesOrder/CS/useGetSalesOrderInit";
import { useGetSalesOrderHeader } from "../../hooks/SalesOrder/useGetSalesOrderHeader";
import { useGetSalesOrderHeaderComparison } from "../../hooks/SalesOrder/useGetSalesOrderHeaderComparison";
import { useGetSalesOrderHeaderStatusD365 } from "../../hooks/SalesOrder/D365/useGetSalesOrderHeaderStatusD365";
import { useGetSalesOrderHeaderChargeComparison } from "../../hooks/SalesOrder/useGetSalesOrderHeaderChargeComparison";
import { useGetCustomerPaymentsByCoOrderD365 } from "../../hooks/CustomerPayment/D365/useGetCustomerPaymentsByCoOrderD365";
import { useGetCustomerComparison } from "../../hooks/Customer/useGetCustomerComparison";
import { useGetVatNumComparison } from "../../hooks/Tax/useGetVatNumComparison";
import { useGetSalesOrderLinesD365 } from "../../hooks/SalesOrderLine/D365/useGetSalesOrderLinesD365";
import { useGetSalesOrderLineExistence, type ILineExistenceInfo } from "../../hooks/SalesOrderLine/useGetSalesOrderLineExistence";
import { getReleasedProductComparison } from "../../hooks/ReleasedProduct/useGetReleasedProductComparison";
import { getDesignItemsByDesignID, type IDesignItemInfo } from "../../hooks/ReleasedProduct/useGetItemsByDesignID";
import { getProductComparison } from "../../hooks/Product/useGetProductComparison";
import { getRouteVersionByItem, type IRouteVersionByItem } from "../../hooks/Route/useGetRouteVersionByItem";
import { getRouteOperationsCS, type IRouteOperationCS } from "../../hooks/Route/useGetRouteOperationsD365";
import { getRouteHeaderExistsD365 } from "../../hooks/Route/useGetRouteHeaderExistsD365";
import { getRouteVersionByItemD365, type IRouteVersionD365 } from "../../hooks/Route/useGetRouteVersionByItemD365";
import { getRouteOperationsByRouteD365, type IRouteOperationD365 } from "../../hooks/Route/useGetRouteOperationsByRouteD365";
import { getRouteOperationPropertiesByRouteD365, type IRouteOperationPropertyD365 } from "../../hooks/Route/useGetRouteOperationPropertiesByRouteD365";
import { getRouteOperationRequirementsByRouteD365, type IRouteOperationRequirementD365 } from "../../hooks/Route/useGetRouteOperationRequirementsByRouteD365";
import { getBomVersionByItemD365, type IBomVersionD365 } from "../../hooks/Route/useGetBomVersionByItemD365";
import { getBomLinesByBomD365, type IBomLineD365 } from "../../hooks/Route/useGetBomLinesByBomD365";
import { getRouteOperationPropertiesCS, type IRouteOperationPropertyCS } from "../../hooks/Route/useGetRouteOperationPropertiesCS";
import { getRouteOperationRequirementsCS, type IRouteOperationRequirementCS } from "../../hooks/Route/useGetRouteOperationRequirementsCS";
import { getBomLinesByRouteCS, type IBomLineCS } from "../../hooks/Route/useGetBomLinesCS";
import { getReleasedProductExistsD365 } from "../../hooks/ReleasedProduct/useGetReleasedProductExistsD365";
import { getReleasedProductSourcingD365, type IReleasedProductSourcing } from "../../hooks/ReleasedProduct/useGetReleasedProductSourcingD365";
import { getRouteHeaderComparison } from "../../hooks/Route/useGetRouteHeaderComparison";
import { getRouteVersionComparison } from "../../hooks/Route/useGetRouteVersionComparison";
import { useGetSalesOrderLineDesigns, type ISalesOrderLineDesignInfo } from "../../hooks/SalesOrderLine/useGetSalesOrderLineDesigns";
import { getManufacturingRoute, type IManufacturingRoute } from "../../hooks/Manufacturing/useGetManufacturingRoute";
import { getProductD365, type IProductD365 } from "../../hooks/Product/useGetProductD365";
import { getReleasedProductD365, type IReleasedProductD365 } from "../../hooks/ReleasedProduct/useGetReleasedProductD365";
import { useExplorerStore } from "../../stores/useExplorerStore";
import { useShallow } from "zustand/react/shallow";
import { applyDagreLayout } from "./useAutoLayout";
import {
    buildComparisonQueryMap,
    buildD365QueryMap,
    computeStatus,
    EMPTY_BOM_LINES_D365,
    EMPTY_OPERATIONS_D365,
    EMPTY_OP_PROPERTIES_D365,
    EMPTY_OP_REQUIREMENTS_D365,
    isValidTaxExemptNumber,
} from "./graphHelpers";
import type { IComparisonInfo } from "../../declarations/types";
import type {
    ExplorerNode,
    ExplorerEdge,
    ExplorerNodeStatus,
} from "../../declarations/IExplorerTypes";
import type { IReadVerbPayload } from "../../declarations/IReadVerbPayload";
import { splitPascalCase } from "../../utilities/ObjectUtilities";

function formatChargeAmount(
    comparisonData: IComparisonInfo[] | undefined,
    freightCharge: number | undefined,
): string {
    const currency = comparisonData?.find(
        (d) => d.targetPropertyName === "ChargeAccountingCurrencyCode",
    )?.targetValue;
    const amount = freightCharge ?? 0;
    return currency ? `${currency} ${amount}` : String(amount);
}

export function useSalesOrderGraph(orderID: number) {
    const selectedNodeId = useExplorerStore((s) => s.selectedNodeId);
    const setSelectedNodeId = useExplorerStore((s) => s.setSelectedNodeId);
    const setResolvedKeys = useExplorerStore((s) => s.setResolvedKeys);
    const {
        expandedLineNumbers,
        expandedReleasedProducts,
        expandedOperations,
        expandedBomLines,
    } = useExplorerStore(useShallow((s) => ({
        expandedLineNumbers: s.expandedLineNumbers,
        expandedReleasedProducts: s.expandedReleasedProducts,
        expandedOperations: s.expandedOperations,
        expandedBomLines: s.expandedBomLines,
    })));
    const toggleLineExpanded = useExplorerStore((s) => s.toggleLineExpanded);
    const toggleReleasedProductExpanded = useExplorerStore((s) => s.toggleReleasedProductExpanded);
    const toggleOperationExpanded = useExplorerStore((s) => s.toggleOperationExpanded);
    const toggleBomLineExpanded = useExplorerStore((s) => s.toggleBomLineExpanded);

    // Ref + version counter used to break the cyclic dependency between d365FallbackRPKeys and
    // d365BomLineMap. The ref holds the latest d365BomLineMap value; the version counter
    // is a stable primitive that triggers d365FallbackRPKeys to recompute when d365BomLineMap changes.
    const d365BomLineMapRef = useRef<Map<string, IBomLineD365[]>>(new Map());
    const [d365BomLineMapVersion, setD365BomLineMapVersion] = useState(0);

    // Fetch init data (resolves CS keys for the order)
    const { data: init, isLoading: initLoading } = useGetSalesOrderInit(orderID);
    const { data: header } = useGetSalesOrderHeader(orderID);

    // Build comparison payload — only valid once init loads
    const headerPayload: IReadVerbPayload = {
        keys: {
            salesOrderNumber: init?.salesOrderNumber ?? "",
            dataAreaId: init?.dataAreaID ?? "",
            orderID: orderID,
        },
    };

    const headerComparison = useGetSalesOrderHeaderComparison(headerPayload);
    const { data: salesOrderStatus } = useGetSalesOrderHeaderStatusD365(headerPayload);

    // Header Charge comparison
    const headerChargePayload: IReadVerbPayload = {
        keys: {
            salesOrderNumber: init?.salesOrderNumber ?? "",
            dataAreaId: init?.dataAreaID ?? "",
            orderID: orderID,
            chargeLineNumber: 1,
        },
    };
    const headerChargeComparison = useGetSalesOrderHeaderChargeComparison(headerChargePayload);

    // Customer Payments — D365-only branch keyed by CoOrderID (== SheaSalesId in D365)
    const customerPaymentsQuery = useGetCustomerPaymentsByCoOrderD365(header?.coOrderID);

    // Customer comparison
    const customerPayload: IReadVerbPayload = {
        keys: {
            dataAreaId: init?.dataAreaID ?? "",
            customerAccount: init?.custAccount ?? "",
            salesAccID: init?.salesAccID ?? 0,
        },
    };
    const customerComparison = useGetCustomerComparison(customerPayload);

    // VAT Number comparison
    const vatNumPayload: IReadVerbPayload = {
        keys: {
            dataAreaId: init?.dataAreaID ?? "",
            countryRegionId: init?.taxCertCountryRegionId ?? "",
            vatNum: init?.taxExemptNumber ?? "",
            salesAccID: init?.salesAccTaxCertID ?? 0,
        },
    };
    const hasValidVatNum = isValidTaxExemptNumber(init?.taxExemptNumber);
    const vatNumComparison = useGetVatNumComparison(vatNumPayload);

    // Sales Order Lines
    const linesPayload: IReadVerbPayload = {
        keys: {
            dataAreaId: init?.dataAreaID ?? "",
            salesOrderNumber: init?.salesOrderNumber ?? "",
            orderID: orderID,
        },
    };
    const { data: linesData, isLoading: linesLoading } = useGetSalesOrderLinesD365(linesPayload);

    // Line existence comparison (CS vs D365)
    const { data: existenceData } = useGetSalesOrderLineExistence(linesPayload);

    // Design info per line (MItemID, DesignID) — used to resolve multi-company released products
    const { data: designData } = useGetSalesOrderLineDesigns(orderID);

    // Build a lookup: inventoryLotId → design info
    const designMap = useMemo(() => {
        const map = new Map<string, ISalesOrderLineDesignInfo>();
        if (designData) {
            for (const d of designData) {
                if (d.inventoryLotId) {
                    map.set(d.inventoryLotId.toLowerCase(), d);
                }
            }
        }
        return map;
    }, [designData]);

    // Collect unique DesignIDs from expanded lines that have DesignID > 0
    const expandedDesignIDs = useMemo(() => {
        const seen = new Set<number>();
        return expandedLineNumbers
            .map((lineNum) => {
                const line = linesData?.find((l) => l.lineNumber === lineNum);
                if (!line?.inventoryLotId) {
                    return 0;
                }
                const design = designMap.get(line.inventoryLotId.toLowerCase());
                return design?.designID ?? 0;
            })
            .filter((id) => {
                if (id <= 0 || seen.has(id)) {
                    return false;
                }
                seen.add(id);
                return true;
            });
    }, [expandedLineNumbers, linesData, designMap]);

    // Fetch items by DesignID — returns all released products across companies for each design
    const itemsByDesignQueries = useQueries({
        queries: expandedDesignIDs.map((designID) => ({
            queryKey: ["GetDesignItemsByDesignID", designID],
            queryFn: () => getDesignItemsByDesignID(designID),
            enabled: designID > 0,
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    // Build lookup: designID → IDesignItemInfo[]
    const itemsByDesignMap = useMemo(() => {
        const map = new Map<number, IDesignItemInfo[]>();
        expandedDesignIDs.forEach((designID, index) => {
            const q = itemsByDesignQueries[index];
            if (q?.data) {
                map.set(designID, q.data);
            }
        });
        return map;
    }, [expandedDesignIDs, itemsByDesignQueries]);

    // Determine which products are manufactured (make) vs purchased/stock (buy)
    // Products without design data or with all buy items are treated as stock items
    const productMakeMap = useMemo(() => {
        const map = new Map<string, boolean>();
        for (const lineNum of expandedLineNumbers) {
            const line = linesData?.find((l) => l.lineNumber === lineNum);
            if (!line?.itemNumber) {
                continue;
            }
            if (map.get(line.itemNumber)) {
                continue; // already determined as make
            }
            const design = line.inventoryLotId
                ? designMap.get(line.inventoryLotId.toLowerCase())
                : undefined;
            const designID = design?.designID ?? 0;
            const items = designID > 0 ? itemsByDesignMap.get(designID) : undefined;
            const isMake = items?.some((i) => i.make) ?? false;
            map.set(line.itemNumber, isMake);
        }
        return map;
    }, [expandedLineNumbers, linesData, designMap, itemsByDesignMap]);

    // Released Product comparisons — only for make items (buy/stock items skip CS comparison)
    // Plus additional queries for cross-company items resolved via DesignID
    const rpComparisonKeys = useMemo(() => {
        const keys: { itemNumber: string; dataAreaId: string; lineNum: number }[] = [];
        for (const lineNum of expandedLineNumbers) {
            const line = linesData?.find((l) => l.lineNumber === lineNum);
            if (!line?.itemNumber) {
                continue;
            }
            // Skip buy/stock items — no CS comparison needed
            if (!productMakeMap.get(line.itemNumber)) {
                continue;
            }
            const design = line.inventoryLotId
                ? designMap.get(line.inventoryLotId.toLowerCase())
                : undefined;
            const designID = design?.designID ?? 0;
            const items = designID > 0 ? itemsByDesignMap.get(designID) : undefined;

            if (items && items.length > 0) {
                // Add a comparison query per cross-company item (item.name is the actual itemNumber)
                for (const item of items) {
                    keys.push({
                        itemNumber: item.name,
                        dataAreaId: item.entity,
                        lineNum,
                    });
                }
            } else {
                // Fallback: single RP from the line's own company
                keys.push({
                    itemNumber: line.itemNumber,
                    dataAreaId: init?.dataAreaID ?? "",
                    lineNum,
                });
            }
        }

        return keys;
    }, [expandedLineNumbers, linesData, designMap, itemsByDesignMap, init?.dataAreaID, productMakeMap]);

    const releasedProductQueries = useQueries({
        queries: rpComparisonKeys.map(({ itemNumber, dataAreaId }) => ({
            queryKey: ["GetReleasedProductComparison", { itemNumber, dataAreaId }],
            queryFn: () =>
                getReleasedProductComparison({
                    keys: { itemNumber, dataAreaId, itemID: 0 },
                }),
            enabled: Boolean(itemNumber && dataAreaId),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    // Product comparisons — only for make products (buy/stock items skip CS comparison)
    // We deduplicate by itemNumber since multiple lines could share the same product
    const uniqueProductNumbers = useMemo(() => {
        const seen = new Set<string>();
        return expandedLineNumbers
            .map((lineNum) => {
                const line = linesData?.find((l) => l.lineNumber === lineNum);
                return line?.itemNumber ?? "";
            })
            .filter((pn) => {
                if (!pn || seen.has(pn)) {
                    return false;
                }
                if (!productMakeMap.get(pn)) {
                    return false; // skip buy/stock products
                }
                seen.add(pn);
                return true;
            });
    }, [expandedLineNumbers, linesData, productMakeMap]);

    const productQueries = useQueries({
        queries: uniqueProductNumbers.map((productNumber) => ({
            queryKey: ["GetProductComparison", { productNumber }],
            queryFn: () =>
                getProductComparison({
                    keys: { productNumber, productID: 0 },
                }),
            enabled: Boolean(productNumber),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    // Build a lookup map: productNumber → product query result
    const productMap = useMemo(
        () => buildComparisonQueryMap(uniqueProductNumbers, productQueries, (pn) => pn),
        [uniqueProductNumbers, productQueries],
    );

    // D365-only queries for buy/stock products (no CS comparison, just D365 data)
    const buyProductNumbers = useMemo(() => {
        const seen = new Set<string>();
        return expandedLineNumbers
            .map((lineNum) => {
                const line = linesData?.find((l) => l.lineNumber === lineNum);
                return line?.itemNumber ?? "";
            })
            .filter((pn) => {
                if (!pn || seen.has(pn)) {
                    return false;
                }
                if (productMakeMap.get(pn)) {
                    return false; // skip make products — they use comparison
                }
                seen.add(pn);
                return true;
            });
    }, [expandedLineNumbers, linesData, productMakeMap]);

    const buyProductQueries = useQueries({
        queries: buyProductNumbers.map((productNumber) => ({
            queryKey: ["GetProductD365", { productNumber }],
            queryFn: () =>
                getProductD365({
                    keys: { productNumber, productID: 0 },
                }),
            enabled: Boolean(productNumber),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    const buyProductMap = useMemo(
        () => buildD365QueryMap<string, IProductD365>(buyProductNumbers, buyProductQueries, (pn) => pn),
        [buyProductNumbers, buyProductQueries],
    );

    // D365-only queries for buy/stock released products (single RP per selling company)
    const buyRPKeys = useMemo(() => {
        const seen = new Set<string>();
        const keys: { itemNumber: string; dataAreaId: string }[] = [];
        for (const lineNum of expandedLineNumbers) {
            const line = linesData?.find((l) => l.lineNumber === lineNum);
            if (!line?.itemNumber) {
                continue;
            }
            if (productMakeMap.get(line.itemNumber)) {
                continue; // skip make products
            }
            const dataAreaId = init?.dataAreaID ?? "";
            const key = `${line.itemNumber}|${dataAreaId}`;
            if (!seen.has(key)) {
                seen.add(key);
                keys.push({ itemNumber: line.itemNumber, dataAreaId });
            }
        }
        return keys;
    }, [expandedLineNumbers, linesData, productMakeMap, init?.dataAreaID]);

    const buyRPQueries = useQueries({
        queries: buyRPKeys.map(({ itemNumber, dataAreaId }) => ({
            queryKey: ["GetReleasedProductD365", { itemNumber, dataAreaId }],
            queryFn: () =>
                getReleasedProductD365({
                    keys: { itemNumber, dataAreaId, itemID: 0 },
                }),
            enabled: Boolean(itemNumber && dataAreaId),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    const buyRPMap = useMemo(
        () => buildD365QueryMap<{ itemNumber: string; dataAreaId: string }, IReleasedProductD365>(
            buyRPKeys,
            buyRPQueries,
            (k) => `${k.itemNumber}|${k.dataAreaId}`,
        ),
        [buyRPKeys, buyRPQueries],
    );

    // Build a lookup map: "itemNumber|dataAreaId" → released product query result
    const releasedProductMap = useMemo(
        () => buildComparisonQueryMap(rpComparisonKeys, releasedProductQueries, (k) => `${k.itemNumber}|${k.dataAreaId}`),
        [rpComparisonKeys, releasedProductQueries],
    );

    // Build a lookup map for existence info by inventoryLotId
    const existenceMap = useMemo(() => {
        const map = new Map<string, ILineExistenceInfo>();
        if (existenceData) {
            for (const item of existenceData) {
                map.set(item.inventoryLotId.toLowerCase(), item);
            }
        }
        return map;
    }, [existenceData]);

    // Route version resolution — for all expanded RPs, resolve RouteId from CS
    // (lightweight CS query; only RPs with a resolved route will show a Route node)
    const expandedRPKeys = useMemo(() => {
        const keys: { itemNumber: string; dataAreaId: string }[] = [];
        const seen = new Set<string>();
        for (const rpKey of expandedReleasedProducts) {
            const [itemNumber, dataAreaId] = rpKey.split("|");
            if (itemNumber && dataAreaId && !seen.has(rpKey)) {
                seen.add(rpKey);
                keys.push({ itemNumber, dataAreaId });
            }
        }
        // BOM-sourced RPs that are expanded also need route resolution
        for (const bomKey of expandedBomLines) {
            if (!seen.has(bomKey)) {
                const [itemNumber, dataAreaId] = bomKey.split("|");
                if (itemNumber && dataAreaId) {
                    seen.add(bomKey);
                    keys.push({ itemNumber, dataAreaId });
                }
            }
        }
        return keys;
    }, [expandedReleasedProducts, expandedBomLines]);

    const routeVersionQueries = useQueries({
        queries: expandedRPKeys.map(({ itemNumber, dataAreaId }) => ({
            queryKey: ["GetRouteVersionByItem", { itemNumber, dataAreaId }],
            queryFn: () => getRouteVersionByItem(itemNumber, dataAreaId),
            enabled: Boolean(itemNumber && dataAreaId),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    // Build lookup: "itemNumber|dataAreaId" → resolved route version info
    const routeVersionMap = useMemo(() => {
        const map = new Map<string, IRouteVersionByItem | null>();
        expandedRPKeys.forEach((key, index) => {
            const q = routeVersionQueries[index];
            if (q?.isFetched) {
                map.set(`${key.itemNumber}|${key.dataAreaId}`, q.data ?? null);
            }
        });
        return map;
    }, [expandedRPKeys, routeVersionQueries]);

    // Route operations — fetch from CS for each resolved route version.
    // dataAreaId comes from the route response (rv.dataAreaID), NOT the rpKey, so cross-company
    // items (e.g. NSG1 purchase products manufactured in NSSL) route their downstream queries
    // to the actual manufacturing company.
    const routeOperationKeys = useMemo(() => {
        const keys: { rpKey: string; csRouteID: number; routeId: string; dataAreaId: string }[] = [];
        for (const [rpKey, rv] of routeVersionMap) {
            if (rv?.csRouteID && rv?.routeId && rv?.dataAreaID) {
                keys.push({ rpKey, csRouteID: rv.csRouteID, routeId: rv.routeId, dataAreaId: rv.dataAreaID });
            }
        }
        return keys;
    }, [routeVersionMap]);

    const routeOperationQueries = useQueries({
        queries: routeOperationKeys.map(({ csRouteID }) => ({
            queryKey: ["GetRouteOperationsCS", csRouteID],
            queryFn: () => getRouteOperationsCS(csRouteID),
            enabled: csRouteID > 0,
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    // Build lookup: "itemNumber|dataAreaId" → { routeId, operations[], isLoading, isError }
    const routeDataMap = useMemo(() => {
        const map = new Map<string, {
            routeId: string;
            operations: IRouteOperationCS[];
            isLoading: boolean;
            isError: boolean;
        }>();
        routeOperationKeys.forEach((key, index) => {
            const q = routeOperationQueries[index];
            map.set(key.rpKey, {
                routeId: key.routeId,
                operations: q?.data ?? [],
                isLoading: q?.isLoading ?? true,
                isError: q?.isError ?? false,
            });
        });
        return map;
    }, [routeOperationKeys, routeOperationQueries]);

    // Route header D365 existence checks — one per resolved route
    const routeExistsQueries = useQueries({
        queries: routeOperationKeys.map(({ routeId, dataAreaId }) => {
            return {
                queryKey: ["GetRouteHeaderExistsD365", { routeId, dataAreaId }],
                queryFn: () => getRouteHeaderExistsD365(routeId, dataAreaId),
                enabled: Boolean(routeId) && Boolean(dataAreaId),
                refetchOnWindowFocus: false,
                retry: false,
            };
        }),
    });

    // Build lookup: rpKey → boolean | null (null = still loading)
    const routeExistsMap = useMemo(() => {
        const map = new Map<string, boolean | null>();
        routeOperationKeys.forEach((key, index) => {
            const q = routeExistsQueries[index];
            map.set(key.rpKey, q?.isFetched ? (q.data === true) : null);
        });
        return map;
    }, [routeOperationKeys, routeExistsQueries]);

    // Route operation properties — fetch from CS for each resolved route version
    const routeOpPropertyQueries = useQueries({
        queries: routeOperationKeys.map(({ csRouteID }) => ({
            queryKey: ["GetRouteOperationPropertiesCS", csRouteID],
            queryFn: () => getRouteOperationPropertiesCS(csRouteID),
            enabled: csRouteID > 0,
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    // Build lookup: rpKey → IRouteOperationPropertyCS[]
    const routeOpPropertyMap = useMemo(() => {
        const map = new Map<string, IRouteOperationPropertyCS[]>();
        routeOperationKeys.forEach((key, index) => {
            const q = routeOpPropertyQueries[index];
            map.set(key.rpKey, q?.data ?? []);
        });
        return map;
    }, [routeOperationKeys, routeOpPropertyQueries]);

    // Route operation requirements — fetch from CS for each resolved route version
    const routeOpRequirementQueries = useQueries({
        queries: routeOperationKeys.map(({ csRouteID }) => ({
            queryKey: ["GetRouteOperationRequirementsCS", csRouteID],
            queryFn: () => getRouteOperationRequirementsCS(csRouteID),
            enabled: csRouteID > 0,
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    // Build lookup: rpKey → IRouteOperationRequirementCS[]
    const routeOpRequirementMap = useMemo(() => {
        const map = new Map<string, IRouteOperationRequirementCS[]>();
        routeOperationKeys.forEach((key, index) => {
            const q = routeOpRequirementQueries[index];
            map.set(key.rpKey, q?.data ?? []);
        });
        return map;
    }, [routeOperationKeys, routeOpRequirementQueries]);

    // BOM lines — fetch from CS for each resolved route version
    const bomLineQueries = useQueries({
        queries: routeOperationKeys.map(({ csRouteID }) => ({
            queryKey: ["GetBomLinesByRouteCS", csRouteID],
            queryFn: () => getBomLinesByRouteCS(csRouteID),
            enabled: csRouteID > 0,
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    // Build lookup: rpKey → IBomLineCS[]
    const bomLineMap = useMemo(() => {
        const map = new Map<string, IBomLineCS[]>();
        routeOperationKeys.forEach((key, index) => {
            const q = bomLineQueries[index];
            map.set(key.rpKey, q?.data ?? []);
        });
        return map;
    }, [routeOperationKeys, bomLineQueries]);

    // D365 existence checks for BOM-sourced Released Products. Declared early so that the
    // sourcing-driven crossCompanyRPKeys (below) can feed into d365RouteAndBomRPKeys, which
    // in turn drives the D365 Route/BOM query setup.
    const bomRPExistenceKeys = useMemo(() => {
        const keys: { itemNumber: string; dataAreaId: string }[] = [];
        const seen = new Set<string>();
        for (const bomKey of expandedBomLines) {
            if (seen.has(bomKey)) {
                continue;
            }
            seen.add(bomKey);
            const [itemNumber, dataAreaId] = bomKey.split("|");
            if (itemNumber && dataAreaId) {
                keys.push({ itemNumber, dataAreaId });
            }
        }
        return keys;
    }, [expandedBomLines]);

    const bomRPExistenceQueries = useQueries({
        queries: bomRPExistenceKeys.map(({ itemNumber, dataAreaId }) => ({
            queryKey: ["GetReleasedProductExistsD365", { itemNumber, dataAreaId }],
            queryFn: () => getReleasedProductExistsD365(itemNumber, dataAreaId),
            enabled: Boolean(itemNumber && dataAreaId),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    // Build lookup: "itemNumber|dataAreaId" → boolean | null (null = still loading)
    const bomRPExistsMap = useMemo(() => {
        const map = new Map<string, boolean | null>();
        bomRPExistenceKeys.forEach((key, index) => {
            const q = bomRPExistenceQueries[index];
            map.set(`${key.itemNumber}|${key.dataAreaId}`, q?.isFetched ? (q.data === true) : null);
        });
        return map;
    }, [bomRPExistenceKeys, bomRPExistenceQueries]);

    // Sourcing for each BOM-derived RP — tells us whether the item is purchased (Purch) or
    // produced (Production) in its dataAreaId. For Purch, exposes the primary vendor and the
    // dataAreaIds where the item is actually manufactured. Drives the Vendor → cross-company
    // RP chain in the explorer.
    const sourcingQueries = useQueries({
        queries: bomRPExistenceKeys.map(({ itemNumber, dataAreaId }) => ({
            queryKey: ["GetReleasedProductSourcingD365", { itemNumber, dataAreaId }],
            queryFn: () => getReleasedProductSourcingD365(itemNumber, dataAreaId),
            enabled: Boolean(itemNumber && dataAreaId),
            staleTime: 10 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    const sourcingMap = useMemo(() => {
        const map = new Map<string, IReleasedProductSourcing | null>();
        bomRPExistenceKeys.forEach((key, index) => {
            const q = sourcingQueries[index];
            map.set(`${key.itemNumber}|${key.dataAreaId}`, q?.isFetched ? (q.data ?? null) : null);
        });
        return map;
    }, [bomRPExistenceKeys, sourcingQueries]);

    // Cross-company manufacturing RP keys derived from sourcing. When a BOM-line RP is Purch
    // with intercompany manufacturers, those manufacturer dataAreaIds need their own D365
    // BomVersion / Route / BomLine queries so the chain can continue across the Vendor.
    const crossCompanyRPKeys = useMemo(() => {
        const keys: { itemNumber: string; dataAreaId: string }[] = [];
        const seen = new Set<string>();
        for (const sourcing of sourcingMap.values()) {
            if (!sourcing?.vendor?.intercompanyDataAreaIds) {
                continue;
            }
            for (const mfgDataAreaId of sourcing.vendor.intercompanyDataAreaIds) {
                const key = `${sourcing.itemNumber}|${mfgDataAreaId}`;
                if (seen.has(key)) {
                    continue;
                }
                seen.add(key);
                keys.push({ itemNumber: sourcing.itemNumber, dataAreaId: mfgDataAreaId });
            }
        }
        return keys;
    }, [sourcingMap]);

    // Phantom BOM-sourced RP keys whose CS routeVersion fetch resolved with null.
    // These are candidates for the D365 fallback chain.
    // d365BomLineMapRef.current is read (not listed as dep) to break the cyclic dependency;
    // d365BomLineMapVersion is the trigger that causes recomputation when the ref is updated.
    const d365FallbackRPKeys = useMemo(() => {
        const keys: { itemNumber: string; dataAreaId: string }[] = [];
        const seen = new Set<string>();
        for (const bomKey of expandedBomLines) {
            if (seen.has(bomKey)) {
                continue;
            }
            // Only fall back when CS resolved null (NOT undefined — undefined means still loading)
            if (routeVersionMap.has(bomKey) && routeVersionMap.get(bomKey) === null) {
                const [itemNumber, dataAreaId] = bomKey.split("|");
                // Verify the BOM line is phantom — check both CS and D365 BOM line maps
                let isPhantom = false;
                // Check CS bomLineMap (b.dataAreaID — capital ID)
                for (const [_rpKey, bomLines] of bomLineMap) {
                    const bl = bomLines.find((b) => b.itemNumber === itemNumber && b.dataAreaID === dataAreaId);
                    if (bl?.stdSailItem) {
                        isPhantom = true;
                        break;
                    }
                }
                // Also check D365 bomLineMap via ref to avoid cyclic dep (b.dataAreaId — lowercase)
                if (!isPhantom) {
                    for (const [_rpKey, d365BomLines] of d365BomLineMapRef.current) {
                        const d365bl = d365BomLines.find((b) => b.itemNumber === itemNumber && b.dataAreaId === dataAreaId);
                        if (d365bl?.lineType === "Phantom") {
                            isPhantom = true;
                            break;
                        }
                    }
                }
                if (isPhantom && itemNumber && dataAreaId) {
                    seen.add(bomKey);
                    keys.push({ itemNumber, dataAreaId });
                }
            }
        }
        return keys;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expandedBomLines, routeVersionMap, bomLineMap, d365BomLineMapVersion]);

    // Combined D365 RP keys that need full Route+BOM data: phantom fallback chain (CS→D365)
    // PLUS cross-company manufacturer RPs reached via Vendor sourcing. Both go through the
    // same D365 queries (RouteVersion, RouteOperations, BomVersion, BomLines), so we just
    // merge them here.
    const d365RouteAndBomRPKeys = useMemo(() => {
        const keys: { itemNumber: string; dataAreaId: string }[] = [];
        const seen = new Set<string>();
        const push = (k: { itemNumber: string; dataAreaId: string }) => {
            const id = `${k.itemNumber}|${k.dataAreaId}`;
            if (!seen.has(id)) {
                seen.add(id);
                keys.push(k);
            }
        };
        d365FallbackRPKeys.forEach(push);
        crossCompanyRPKeys.forEach(push);
        return keys;
    }, [d365FallbackRPKeys, crossCompanyRPKeys]);

    const d365RouteVersionQueries = useQueries({
        queries: d365RouteAndBomRPKeys.map(({ itemNumber, dataAreaId }) => ({
            queryKey: ["GetRouteVersionByItemD365", { itemNumber, dataAreaId }],
            queryFn: () => getRouteVersionByItemD365(itemNumber, dataAreaId),
            enabled: Boolean(itemNumber && dataAreaId),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    const d365RouteVersionMap = useMemo(() => {
        const map = new Map<string, IRouteVersionD365 | null>();
        d365RouteAndBomRPKeys.forEach((key, index) => {
            const q = d365RouteVersionQueries[index];
            if (q?.isFetched) {
                map.set(`${key.itemNumber}|${key.dataAreaId}`, q.data ?? null);
            }
        });
        return map;
    }, [d365RouteAndBomRPKeys, d365RouteVersionQueries]);

    const d365RouteKeys = useMemo(() => {
        const keys: { rpKey: string; routeId: string; dataAreaId: string }[] = [];
        for (const [rpKey, rv] of d365RouteVersionMap) {
            if (rv?.routeId && rv?.dataAreaId) {
                // dataAreaId values are stored lowercase in D365 OData and the filter is
                // case-sensitive — pass through unchanged.
                keys.push({ rpKey, routeId: rv.routeId, dataAreaId: rv.dataAreaId });
            }
        }
        return keys;
    }, [d365RouteVersionMap]);

    const d365RouteOperationQueries = useQueries({
        queries: d365RouteKeys.map(({ routeId, dataAreaId }) => ({
            queryKey: ["GetRouteOperationsByRouteD365", { routeId, dataAreaId }],
            queryFn: () => getRouteOperationsByRouteD365(routeId, dataAreaId),
            enabled: Boolean(routeId) && Boolean(dataAreaId),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    const d365RouteOperationsMap = useMemo(() => {
        const map = new Map<string, { routeId: string; operations: IRouteOperationD365[]; isLoading: boolean; isError: boolean }>();
        d365RouteKeys.forEach((key, index) => {
            const q = d365RouteOperationQueries[index];
            const raw = q?.data;
            const operations = raw && raw.length > 0
                ? [...raw].sort((a, b) => a.operationNumber - b.operationNumber)
                : EMPTY_OPERATIONS_D365;
            map.set(key.rpKey, {
                routeId: key.routeId,
                operations,
                isLoading: q?.isLoading ?? true,
                isError: q?.isError ?? false,
            });
        });
        return map;
    }, [d365RouteKeys, d365RouteOperationQueries]);

    const d365OpPropertyQueries = useQueries({
        queries: d365RouteKeys.map(({ routeId, dataAreaId }) => ({
            queryKey: ["GetRouteOperationPropertiesByRouteD365", { routeId, dataAreaId }],
            queryFn: () => getRouteOperationPropertiesByRouteD365(routeId, dataAreaId),
            enabled: Boolean(routeId) && Boolean(dataAreaId),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    const d365OpPropertyMap = useMemo(() => {
        const map = new Map<string, { data: IRouteOperationPropertyD365[]; isLoading: boolean; isFetched: boolean }>();
        d365RouteKeys.forEach((key, index) => {
            const q = d365OpPropertyQueries[index];
            map.set(key.rpKey, {
                data: q?.data ?? EMPTY_OP_PROPERTIES_D365,
                isLoading: q?.isLoading ?? false,
                isFetched: q?.isFetched ?? false,
            });
        });
        return map;
    }, [d365RouteKeys, d365OpPropertyQueries]);

    const d365OpRequirementQueries = useQueries({
        queries: d365RouteKeys.map(({ routeId, dataAreaId }) => ({
            queryKey: ["GetRouteOperationRequirementsByRouteD365", { routeId, dataAreaId }],
            queryFn: () => getRouteOperationRequirementsByRouteD365(routeId, dataAreaId),
            enabled: Boolean(routeId) && Boolean(dataAreaId),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    const d365OpRequirementMap = useMemo(() => {
        const map = new Map<string, IRouteOperationRequirementD365[]>();
        d365RouteKeys.forEach((key, index) => {
            map.set(key.rpKey, d365OpRequirementQueries[index]?.data ?? EMPTY_OP_REQUIREMENTS_D365);
        });
        return map;
    }, [d365RouteKeys, d365OpRequirementQueries]);

    const d365BomVersionQueries = useQueries({
        queries: d365RouteKeys.map(({ rpKey, dataAreaId }) => {
            // Use the resolved D365 route's dataAreaId (manufacturing company), not the parent's.
            const itemNumber = rpKey.split("|")[0];
            return {
                queryKey: ["GetBomVersionByItemD365", { itemNumber, dataAreaId }],
                queryFn: () => getBomVersionByItemD365(itemNumber, dataAreaId),
                enabled: Boolean(itemNumber && dataAreaId),
                staleTime: 5 * 60 * 1000,
                refetchOnWindowFocus: false,
                retry: false,
            };
        }),
    });

    const d365BomVersionMap = useMemo(() => {
        const map = new Map<string, IBomVersionD365 | null>();
        d365RouteKeys.forEach((key, index) => {
            const q = d365BomVersionQueries[index];
            if (q?.isFetched) {
                map.set(key.rpKey, q.data ?? null);
            }
        });
        return map;
    }, [d365RouteKeys, d365BomVersionQueries]);

    const d365BomKeys = useMemo(() => {
        const keys: { rpKey: string; bomId: string; dataAreaId: string }[] = [];
        for (const [rpKey, bv] of d365BomVersionMap) {
            if (bv?.bomId && bv?.dataAreaId) {
                // Pass dataAreaId through unchanged (D365 stores it lowercase).
                keys.push({ rpKey, bomId: bv.bomId, dataAreaId: bv.dataAreaId });
            }
        }
        return keys;
    }, [d365BomVersionMap]);

    const d365BomLineQueries = useQueries({
        queries: d365BomKeys.map(({ bomId, dataAreaId }) => ({
            queryKey: ["GetBomLinesByBomD365", { bomId, dataAreaId }],
            queryFn: () => getBomLinesByBomD365(bomId, dataAreaId),
            enabled: Boolean(bomId) && Boolean(dataAreaId),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    const d365BomLineMap = useMemo(() => {
        const map = new Map<string, IBomLineD365[]>();
        d365BomKeys.forEach((key, index) => {
            map.set(key.rpKey, d365BomLineQueries[index]?.data ?? EMPTY_BOM_LINES_D365);
        });
        return map;
    }, [d365BomKeys, d365BomLineQueries]);

    // Sync ref and bump version counter whenever d365BomLineMap content changes.
    // CONTENT comparison (not reference) is required: the map is recreated every render
    // by upstream useMemo+useQueries chains even when no data actually changed. Bumping
    // version on every reference change would cause an infinite render loop because
    // d365FallbackRPKeys depends on the version, and its output cascades back through the
    // chain to produce a new d365BomLineMap reference.
    useEffect(() => {
        const prev = d365BomLineMapRef.current;
        let changed = d365BomLineMap.size !== prev.size;
        if (!changed) {
            for (const [key, lines] of d365BomLineMap) {
                const prevLines = prev.get(key);
                if (!prevLines || prevLines !== lines) {
                    changed = true;
                    break;
                }
            }
        }
        if (changed) {
            d365BomLineMapRef.current = d365BomLineMap;
            setD365BomLineMapVersion((v) => v + 1);
        }
    }, [d365BomLineMap]);

    // Route Header + Version comparison queries — one pair per resolved route.
    // dataAreaId comes from the resolved route version (manufacturing company), itemNumber
    // from rpKey (the BOM line's reference, which uses the parent's dataArea but the same
    // item number as the manufacturing company's row).
    const routeHeaderComparisonQueries = useQueries({
        queries: routeOperationKeys.map(({ csRouteID, routeId, dataAreaId }) => {
            return {
                queryKey: ["GetRouteHeaderComparison", { csRouteID, routeId, dataAreaId }],
                queryFn: () => getRouteHeaderComparison(csRouteID, routeId, dataAreaId),
                enabled: csRouteID > 0 && Boolean(routeId) && Boolean(dataAreaId),
                refetchOnWindowFocus: false,
                retry: false,
            };
        }),
    });

    const routeVersionComparisonQueries = useQueries({
        queries: routeOperationKeys.map(({ rpKey, csRouteID, routeId, dataAreaId }) => {
            const itemNumber = rpKey.split("|")[0];
            const rv = routeVersionMap.get(rpKey);
            return {
                queryKey: ["GetRouteVersionComparison", { csRouteID, routeId, dataAreaId, itemNumber }],
                queryFn: () => getRouteVersionComparison(csRouteID, routeId, dataAreaId, rv?.productionSiteId ?? "", itemNumber),
                enabled: csRouteID > 0 && Boolean(routeId) && Boolean(dataAreaId) && Boolean(itemNumber),
                refetchOnWindowFocus: false,
                retry: false,
            };
        }),
    });

    // Build lookup: rpKey → { headerData?, versionData? }
    const routeComparisonMap = useMemo(() => {
        const map = new Map<string, { headerData?: IComparisonInfo[]; versionData?: IComparisonInfo[]; isLoading: boolean }>();
        routeOperationKeys.forEach((key, index) => {
            const hq = routeHeaderComparisonQueries[index];
            const vq = routeVersionComparisonQueries[index];
            map.set(key.rpKey, {
                headerData: hq?.data,
                versionData: vq?.data,
                isLoading: (hq?.isLoading ?? true) || (vq?.isLoading ?? true),
            });
        });
        return map;
    }, [routeOperationKeys, routeHeaderComparisonQueries, routeVersionComparisonQueries]);

    // BOM RP comparison queries — only for "make" items (stdSailItem === true)
    const bomRPComparisonKeys = useMemo(() => {
        const keys: { itemNumber: string; dataAreaId: string }[] = [];
        const seen = new Set<string>();
        for (const bomKey of expandedBomLines) {
            if (seen.has(bomKey)) {
                continue;
            }
            const [itemNumber, dataAreaId] = bomKey.split("|");
            if (!itemNumber || !dataAreaId) {
                continue;
            }
            // Skip when CS has no route version for this item — the comparison endpoint
            // resolves ItemID via vwD3ReleasedProductV2 which requires the item to be a
            // Make=1 released product in CS. Phantoms inline in CS BOMs aren't standalone
            // released products, so the resolve fails with NOT_FOUND. The truthy check
            // also defers the comparison until CS has finished its lookup.
            if (!routeVersionMap.get(bomKey)) {
                continue;
            }
            // Check if this BOM line is a "make" item (stdSailItem === true)
            // Look through all bomLineMap entries to find this item
            let isMakeItem = false;
            for (const [_rpKey, bomLines] of bomLineMap) {
                const bl = bomLines.find((b) => b.itemNumber === itemNumber && b.dataAreaID === dataAreaId);
                if (bl?.stdSailItem) {
                    isMakeItem = true;
                    break;
                }
            }
            if (isMakeItem) {
                seen.add(bomKey);
                keys.push({ itemNumber, dataAreaId });
            }
        }
        return keys;
    }, [expandedBomLines, bomLineMap, routeVersionMap]);

    const bomRPComparisonQueries = useQueries({
        queries: bomRPComparisonKeys.map(({ itemNumber, dataAreaId }) => ({
            queryKey: ["GetReleasedProductComparison", { itemNumber, dataAreaId }],
            queryFn: () =>
                getReleasedProductComparison({
                    keys: { itemNumber, dataAreaId, itemID: 0 },
                }),
            enabled: Boolean(itemNumber && dataAreaId),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    // Build lookup: "itemNumber|dataAreaId" → BOM RP comparison result
    const bomRPComparisonMap = useMemo(
        () => buildComparisonQueryMap(bomRPComparisonKeys, bomRPComparisonQueries, (k) => `${k.itemNumber}|${k.dataAreaId}`),
        [bomRPComparisonKeys, bomRPComparisonQueries],
    );

    // FM Manufacturing Route — collect unique mItemIDs from expanded make lines only
    const expandedMItemIDs = useMemo(() => {
        const seen = new Set<number>();
        const result: { mItemID: number; lineNumber: number }[] = [];
        for (const lineNum of expandedLineNumbers) {
            const line = linesData?.find((l) => l.lineNumber === lineNum);
            if (!line?.inventoryLotId || !line?.itemNumber) {
                continue;
            }
            if (!productMakeMap.get(line.itemNumber)) {
                continue; // skip buy/stock items
            }
            const design = designMap.get(line.inventoryLotId.toLowerCase());
            const mItemID = design?.mItemID ?? 0;
            if (mItemID > 0 && !seen.has(mItemID)) {
                seen.add(mItemID);
                result.push({ mItemID, lineNumber: lineNum });
            }
        }
        return result;
    }, [expandedLineNumbers, linesData, designMap, productMakeMap]);

    const fmRouteQueries = useQueries({
        queries: expandedMItemIDs.map(({ mItemID }) => ({
            queryKey: ["GetManufacturingRoute", mItemID],
            queryFn: () => getManufacturingRoute(mItemID),
            enabled: mItemID > 0,
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    // Build lookup: lineNumber → { data?, isLoading, isError }
    const fmRouteMap = useMemo(() => {
        const map = new Map<number, { data?: IManufacturingRoute | null; isLoading: boolean; isError: boolean }>();
        expandedMItemIDs.forEach(({ lineNumber }, index) => {
            const q = fmRouteQueries[index];
            map.set(lineNumber, {
                data: q?.data,
                isLoading: q?.isLoading ?? true,
                isError: q?.isError ?? false,
            });
        });
        return map;
    }, [expandedMItemIDs, fmRouteQueries]);

    // Store resolved keys once init is loaded
    useEffect(() => {
        if (init) {
            setResolvedKeys({
                orderID: init.orderID,
                salesOrderNumber: init.salesOrderNumber,
                dataAreaId: init.dataAreaID,
                custAccount: init.custAccount,
                salesAccID: init.salesAccID,
                salesAccTaxCertID: init.salesAccTaxCertID,
                taxCertCountryRegionId: init.taxCertCountryRegionId,
                taxExemptNumber: init.taxExemptNumber,
                freightCharge: init.freightCharge,
                hasInterCompanyOrder: init.hasInterCompanyOrder,
            });
        }
    }, [init, setResolvedKeys]);

    const onNodeSelect = useCallback(
        (nodeId: string) => {
            const current = useExplorerStore.getState().selectedNodeId;
            setSelectedNodeId(nodeId === current ? null : nodeId);
        },
        [setSelectedNodeId],
    );

    const onLineSelect = useCallback(
        (lineNumber: number) => {
            toggleLineExpanded(lineNumber);
        },
        [toggleLineExpanded],
    );

    const onReleasedProductSelect = useCallback(
        (nodeId: string) => {
            // nodeId is "ReleasedProduct-itemNumber|dataAreaId"
            const rpKey = nodeId.replace("ReleasedProduct-", "");
            toggleReleasedProductExpanded(rpKey);
        },
        [toggleReleasedProductExpanded],
    );

    const onOperationSelect = useCallback(
        (rpKey: string, operationNumber: number) => {
            const opKey = `${rpKey}|${operationNumber}`;
            toggleOperationExpanded(opKey);
        },
        [toggleOperationExpanded],
    );

    const onBomLineSelect = useCallback(
        (itemNumber: string, dataAreaId: string) => {
            const bomLineKey = `${itemNumber}|${dataAreaId}`;
            toggleBomLineExpanded(bomLineKey);
        },
        [toggleBomLineExpanded],
    );

    // Build graph
    const rawGraph = useMemo(() => {
        const headerStatus = computeStatus(
            headerComparison.data,
            headerComparison.isLoading,
            headerComparison.isError,
            headerComparison.isFetched,
        );

        const soHeaderNode: ExplorerNode = {
            id: "SalesOrderHeader",
            type: "entityNode",
            position: { x: 0, y: 0 },
            data: {
                entityType: "SalesOrderHeader",
                label: header?.coOrderID
                    ? `Sales Order ${header.coOrderID}`
                    : "Sales Order",
                nodeTitle: "Sales Order",
                nodeSubtitle: header?.coOrderID ?? undefined,
                status: initLoading ? "loading" : headerStatus.status,
                dataAreaId: header?.dataAreaId ?? init?.dataAreaID,
                summaryFields: [
                    { label: "Contact", value: header?.contact ?? "—" },
                    { label: "Boat", value: header?.boat ?? "—" },
                ],
                orderStatus: salesOrderStatus ?? undefined,
                discrepancyCount: headerStatus.discrepancyCount,
                totalFields: headerStatus.totalFields,
                isSelected: false,
                onSelect: onNodeSelect,
                d365Exists: headerComparison.isFetched
                    ? (headerComparison.data !== undefined && !headerComparison.isError)
                    : null,
            },
        };

        // Header Charge node
        const headerChargeStatus = computeStatus(
            headerChargeComparison.data,
            headerChargeComparison.isLoading,
            headerChargeComparison.isError,
            headerChargeComparison.isFetched,
        );

        const headerChargeNode: ExplorerNode = {
            id: "SalesOrderHeaderCharge",
            type: "entityNode",
            position: { x: 0, y: 0 },
            data: {
                entityType: "SalesOrderHeaderCharge",
                label: "Header Charges",
                nodeTitle: "Header Charges",
                nodeSubtitle: header?.coOrderID ?? undefined,
                status: initLoading ? "loading" : headerChargeStatus.status,
                dataAreaId: init?.dataAreaID,
                summaryFields: [
                    { label: "Freight Charge", value: formatChargeAmount(headerChargeComparison.data, init?.freightCharge) },
                ],
                discrepancyCount: headerChargeStatus.discrepancyCount,
                totalFields: headerChargeStatus.totalFields,
                isSelected: false,
                onSelect: onNodeSelect,
                d365Exists: headerChargeComparison.isFetched
                    ? (headerChargeComparison.data !== undefined && !headerChargeComparison.isError)
                    : null,
            },
        };

        // Customer Payments node (D365-only — empty list still renders)
        const paymentsRaw = customerPaymentsQuery.data ?? [];
        const paymentsItems = paymentsRaw.map((p) => ({
            voucher: p.voucher,
            transDate: p.transDate,
            amountCur: p.amountCur,
            currencyCode: p.currencyCode,
            accountNum: p.accountNum,
            dataAreaId: p.dataAreaId,
            companyBankAccountId: p.companyBankAccountId,
            createdBy1: p.createdBy1,
        }));
        const paymentsTotal = paymentsRaw.reduce((sum, p) => sum + (p.amountCur ?? 0), 0);
        const paymentsCurrency = paymentsRaw[0]?.currencyCode;
        const paymentsStatus: ExplorerNodeStatus = customerPaymentsQuery.isLoading
            ? "loading"
            : customerPaymentsQuery.isError
                ? "error"
                : "na";

        const customerPaymentsNode: ExplorerNode = {
            id: "CustomerPayments",
            type: "customerPaymentsNode",
            position: { x: 0, y: 0 },
            data: {
                entityType: "CustomerPayments",
                label: "Customer Payments",
                nodeTitle: "Customer Payments",
                nodeSubtitle: header?.coOrderID ?? undefined,
                status: paymentsStatus,
                dataAreaId: init?.dataAreaID,
                summaryFields: [],
                isSelected: false,
                onSelect: onNodeSelect,
                source: "d365",
                payments: paymentsItems,
                paymentsTotal,
                paymentsCurrency,
            },
        };

        // Customer node
        const customerStatus = computeStatus(
            customerComparison.data,
            customerComparison.isLoading,
            customerComparison.isError,
            customerComparison.isFetched,
        );

        const customerPartyNumber = customerComparison.data?.find(
            (d) => d.targetPropertyName === "PartyNumber",
        )?.targetValue as string | undefined;
        const customerOrgName = customerComparison.data?.find(
            (d) => d.targetPropertyName === "OrganizationName",
        )?.targetValue as string | undefined;
        const customerPartyType = customerComparison.data?.find(
            (d) => d.targetPropertyName === "PartyType",
        )?.targetValue as string | undefined;

        const customerNode: ExplorerNode = {
            id: "Customer",
            type: "entityNode",
            position: { x: 0, y: 0 },
            data: {
                entityType: "Customer",
                label: customerPartyNumber
                    ? `Customer ${customerPartyNumber}`
                    : init?.custAccount
                        ? `Customer ${init.custAccount}`
                        : "Customer",
                nodeTitle: "Customer",
                nodeSubtitle: customerPartyNumber ?? init?.custAccount ?? undefined,
                status: initLoading ? "loading" : customerStatus.status,
                dataAreaId: init?.dataAreaID,
                summaryFields: [
                    { label: "Organization", value: customerOrgName ?? "—" },
                    { label: "Party Type", value: customerPartyType ?? "—" },
                ],
                discrepancyCount: customerStatus.discrepancyCount,
                totalFields: customerStatus.totalFields,
                isSelected: false,
                onSelect: onNodeSelect,
                d365Exists: customerComparison.isFetched
                    ? (customerComparison.data !== undefined && !customerComparison.isError)
                    : null,
            },
        };

        // VatNum node
        const vatNumStatus = computeStatus(
            vatNumComparison.data,
            vatNumComparison.isLoading,
            vatNumComparison.isError,
            vatNumComparison.isFetched,
        );

        const vatNumNode: ExplorerNode = {
            id: "VatNum",
            type: "entityNode",
            position: { x: 0, y: 0 },
            data: {
                entityType: "VatNum",
                label: "VAT Number",
                nodeTitle: "VAT Number",
                status: initLoading ? "loading" : vatNumStatus.status,
                dataAreaId: init?.dataAreaID,
                summaryFields: [
                    { label: "Tax Exempt #", value: init?.taxExemptNumber ?? "—" },
                    { label: "Country", value: init?.taxCertCountryRegionId ?? "—" },
                ],
                discrepancyCount: vatNumStatus.discrepancyCount,
                totalFields: vatNumStatus.totalFields,
                isSelected: false,
                onSelect: onNodeSelect,
                d365Exists: vatNumComparison.isFetched
                    ? (vatNumComparison.data !== undefined && !vatNumComparison.isError)
                    : null,
            },
        };

        // Sales Order Lines list node
        const lineItems = (linesData ?? []).map((line) => {
            const existence = line.inventoryLotId
                ? existenceMap.get(line.inventoryLotId.toLowerCase())
                : undefined;
            return {
                lineNumber: line.lineNumber,
                itemNumber: line.itemNumber,
                externalItemNumber: line.externalItemNumber,
                lineDescription: line.lineDescription || line.lineName,
                quantity: line.orderedSalesQuantity,
                salesOrderLineStatus: line.salesOrderLineStatus,
                isExpanded: expandedLineNumbers.includes(line.lineNumber),
                existsInCS: existence?.existsInCS,
                existsInD365: existence?.existsInD365,
            };
        });

        const linesNode: ExplorerNode = {
            id: "SalesOrderLines",
            type: "salesOrderLinesNode",
            position: { x: 0, y: 0 },
            data: {
                entityType: "SalesOrderLines",
                label: "Sales Order Lines",
                nodeTitle: "Sales Order Lines",
                nodeSubtitle: header?.coOrderID ?? undefined,
                dataAreaId: init?.dataAreaID,
                status: linesLoading ? "loading" : (linesData ? "synced" : "na"),
                summaryFields: [],
                lines: lineItems,
                onLineSelect: onLineSelect,
                isSelected: false,
                onSelect: onNodeSelect,
            },
        };

        // Line detail nodes for expanded lines
        const lineDetailNodes: ExplorerNode[] = expandedLineNumbers
            .map((lineNum) => {
                const line = linesData?.find((l) => l.lineNumber === lineNum);
                if (!line) {
                    return null;
                }
                const nodeId = `SalesOrderLineDetail-${lineNum}`;
                return {
                    id: nodeId,
                    type: "lineDetailNode",
                    position: { x: 0, y: 0 },
                    data: {
                        entityType: "SalesOrderLineDetail" as const,
                        label: `Sales Order Line ${line.externalItemNumber || line.itemNumber}`,
                        nodeTitle: "Sales Order Line",
                        nodeSubtitle: line.externalItemNumber || line.itemNumber,
                        status: "synced" as const,
                        summaryFields: [],
                        lineDetail: {
                            lineNumber: line.lineNumber,
                            status: line.salesOrderLineStatus,
                            itemNumber: line.itemNumber,
                            externalItemNumber: line.externalItemNumber,
                            lineDescription: line.lineDescription || line.lineName,
                            quantity: line.orderedSalesQuantity,
                            salesUnitSymbol: line.salesUnitSymbol,
                            salesPrice: line.salesPrice,
                            lineAmount: line.lineAmount,
                            currencyCode: line.currencyCode,
                            inventoryLotId: line.inventoryLotId,
                            dataAreaId: init?.dataAreaID ?? "",
                            orderID: orderID,
                            existsInCS: line.inventoryLotId
                                ? existenceMap.get(line.inventoryLotId.toLowerCase())?.existsInCS
                                : undefined,
                            existsInD365: line.inventoryLotId
                                ? existenceMap.get(line.inventoryLotId.toLowerCase())?.existsInD365
                                : undefined,
                        },
                        isSelected: false,
                        onSelect: onNodeSelect,
                    },
                } as ExplorerNode;
            })
            .filter((n): n is ExplorerNode => n !== null);

        const lineDetailEdges: ExplorerEdge[] = lineDetailNodes.map((n) => ({
            id: `e-line-${n.id}`,
            source: "SalesOrderLines",
            target: n.id,
        }));

        // Group expanded lines by productNumber for shared Product + ReleasedProduct nodes
        const productLineMap = new Map<string, number[]>();
        for (const lineNum of expandedLineNumbers) {
            const line = linesData?.find((l) => l.lineNumber === lineNum);
            if (!line?.itemNumber) {
                continue;
            }
            const existing = productLineMap.get(line.itemNumber);
            if (existing) {
                existing.push(lineNum);
            } else {
                productLineMap.set(line.itemNumber, [lineNum]);
            }
        }

        // Product nodes — one per unique ProductNumber, connected from line details
        // Make items: CS comparison + RP list. Buy/stock items: no comparison + single RP.
        const productNodes: ExplorerNode[] = [];
        const productEdges: ExplorerEdge[] = [];

        for (const [productNumber, lineNums] of productLineMap) {
            const isMake = productMakeMap.get(productNumber) ?? false;

            let pStatus: { status: ExplorerNodeStatus; discrepancyCount: number; totalFields: number };
            let d365Exists: boolean | null | undefined;

            if (isMake) {
                const pQuery = productMap.get(productNumber);
                pStatus = pQuery
                    ? computeStatus(pQuery.data, pQuery.isLoading, pQuery.isError, pQuery.isFetched)
                    : { status: "na" as const, discrepancyCount: 0, totalFields: 0 };
                d365Exists = pQuery?.isFetched
                    ? (pQuery.data !== undefined && !pQuery.isError)
                    : null;
            } else {
                const buyQuery = buyProductMap.get(productNumber);
                pStatus = buyQuery?.isLoading
                    ? { status: "loading" as const, discrepancyCount: 0, totalFields: 0 }
                    : buyQuery?.isError
                        ? { status: "error" as const, discrepancyCount: 0, totalFields: 0 }
                        : { status: "na" as const, discrepancyCount: 0, totalFields: 0 };
                d365Exists = buyQuery?.data ? true : buyQuery?.isError ? false : undefined;
            }

            const nodeId = `Product-${productNumber}`;
            productNodes.push({
                id: nodeId,
                type: "entityNode",
                position: { x: 0, y: 0 },
                data: {
                    entityType: "Product" as const,
                    label: `Product ${productNumber}`,
                    nodeTitle: "Product",
                    nodeSubtitle: productNumber,
                    status: pStatus.status,
                    summaryFields: [
                        { label: "Product Number", value: productNumber },
                    ],
                    discrepancyCount: pStatus.discrepancyCount,
                    totalFields: pStatus.totalFields,
                    isSelected: false,
                    onSelect: onNodeSelect,
                    d365Exists,
                },
            } as ExplorerNode);

            // Edges from each line detail → Product
            for (const lineNum of lineNums) {
                productEdges.push({
                    id: `e-prod-${lineNum}`,
                    source: `SalesOrderLineDetail-${lineNum}`,
                    target: nodeId,
                });
            }
        }

        // Released Product nodes — make items get a list node (multi-company), buy items get a single entity node
        const releasedProductNodes: ExplorerNode[] = [];
        const releasedProductEdges: ExplorerEdge[] = [];

        for (const [productNumber, lineNums] of productLineMap) {
            const isMake = productMakeMap.get(productNumber) ?? false;

            if (isMake) {
                // Make item: RP list node with design-resolved items across companies
                const rpListNodeId = `ReleasedProducts-${productNumber}`;

                // Resolve DesignID from the first line's design info
                const firstLine = linesData?.find((l) => l.lineNumber === lineNums[0]);
                const firstDesign = firstLine?.inventoryLotId
                    ? designMap.get(firstLine.inventoryLotId.toLowerCase())
                    : undefined;
                const designID = firstDesign?.designID ?? 0;
                const designItems = designID > 0 ? itemsByDesignMap.get(designID) : undefined;

                // Build RP items: if we have design-resolved items, show all companies; else fallback to line's company
                const rpItems = designItems && designItems.length > 0
                    ? designItems.map((item, idx) => {
                        const mapKey = `${item.name}|${item.entity}`;
                        const rpQuery = releasedProductMap.get(mapKey);
                        const comparisonOk = rpQuery?.data !== undefined && !rpQuery?.isError;

                        return {
                            itemNumber: item.name,
                            dataAreaId: item.entity,
                            lineNumber: idx + 1,
                            make: item.make,
                            existsInCS: rpQuery?.isFetched ? comparisonOk : undefined,
                            existsInD365: rpQuery?.isFetched ? comparisonOk : undefined,
                        };
                    })
                    : lineNums.map((lineNum) => {
                        const line = linesData?.find((l) => l.lineNumber === lineNum);
                        const dataAreaId = init?.dataAreaID ?? "";
                        const mapKey = `${productNumber}|${dataAreaId}`;
                        const rpQuery = releasedProductMap.get(mapKey);
                        const comparisonOk = rpQuery?.data !== undefined && !rpQuery?.isError;

                        return {
                            itemNumber: line?.itemNumber ?? "",
                            dataAreaId,
                            lineNumber: lineNum,
                            existsInCS: rpQuery?.isFetched ? comparisonOk : undefined,
                            existsInD365: rpQuery?.isFetched ? comparisonOk : undefined,
                        };
                    });

                const anyLoading = rpItems.some((i) => i.existsInCS === undefined);
                const overallStatus = anyLoading ? "loading" : "synced";

                releasedProductNodes.push({
                    id: rpListNodeId,
                    type: "releasedProductListNode",
                    position: { x: 0, y: 0 },
                    data: {
                        entityType: "ReleasedProduct" as const,
                        label: "Released Products",
                        nodeTitle: "Released Products",
                        nodeSubtitle: productNumber,
                        status: overallStatus as ExplorerNodeStatus,
                        summaryFields: [],
                        releasedProducts: rpItems,
                        expandedReleasedProducts,
                        isSelected: false,
                        onSelect: onNodeSelect,
                        onReleasedProductSelect: onReleasedProductSelect,
                    },
                } as ExplorerNode);

                releasedProductEdges.push({
                    id: `e-rp-${productNumber}`,
                    source: `Product-${productNumber}`,
                    target: rpListNodeId,
                });
            } else {
                // Buy/stock item: single Released Product entity node for the selling company
                const dataAreaId = init?.dataAreaID ?? "";
                const rpNodeId = `ReleasedProduct-${productNumber}|${dataAreaId}`;
                const buyRPQuery = buyRPMap.get(`${productNumber}|${dataAreaId}`);
                const buyRPStatus: ExplorerNodeStatus = buyRPQuery?.isLoading
                    ? "loading"
                    : buyRPQuery?.isError
                        ? "error"
                        : "na";

                releasedProductNodes.push({
                    id: rpNodeId,
                    type: "entityNode",
                    position: { x: 0, y: 0 },
                    data: {
                        entityType: "ReleasedProduct" as const,
                        label: "Released Product",
                        nodeTitle: "Released Product",
                        nodeSubtitle: productNumber,
                        status: buyRPStatus,
                        dataAreaId,
                        summaryFields: [
                            { label: "Product Number", value: productNumber },
                            { label: "Company", value: dataAreaId },
                        ],
                        discrepancyCount: 0,
                        totalFields: 0,
                        isSelected: false,
                        onSelect: onNodeSelect,
                        d365Exists: buyRPQuery?.data ? true : buyRPQuery?.isError ? false : undefined,
                    },
                } as ExplorerNode);

                releasedProductEdges.push({
                    id: `e-rp-${productNumber}`,
                    source: `Product-${productNumber}`,
                    target: rpNodeId,
                });
            }
        }

        // Expanded Released Product entity nodes — individual RP nodes that fan out from the list
        const expandedRPNodes: ExplorerNode[] = [];
        const expandedRPEdges: ExplorerEdge[] = [];
        const routeNodes: ExplorerNode[] = [];
        const routeEdges: ExplorerEdge[] = [];

        const visitedRPKeys = new Set<string>();
        const visitedVendorKeys = new Set<string>();

        /**
         * Inserts a Vendor → cross-company RP chain off the given source RP node when sourcing
         * resolves DefaultOrderType=Purch. Returns true when the chain was inserted (the caller
         * should NOT recurse with its own Route/BOM chain — the manufacturing company's data
         * lives off the cross-company RP). Returns false when sourcing is missing, still loading,
         * or the item is produced in the parent's company (the existing chain applies).
         */
        function tryInsertVendorChain(rpKey: string, sourceRpNodeId: string, edgePrefix: string): boolean {
            const sourcing = sourcingMap.get(rpKey);
            if (!sourcing) {
                return false;
            }
            if (sourcing.defaultOrderType?.toLowerCase() !== "purch" || !sourcing.vendor) {
                return false;
            }

            const vendor = sourcing.vendor;
            const vendorNodeId = `Vendor-${rpKey}`;

            if (!visitedVendorKeys.has(vendorNodeId)) {
                visitedVendorKeys.add(vendorNodeId);
                const vendorDisplayName = vendor.vendorKnownAsName
                    || vendor.vendorName
                    || vendor.vendorAccountNumber;
                routeNodes.push({
                    id: vendorNodeId,
                    type: "entityNode",
                    position: { x: 0, y: 0 },
                    data: {
                        entityType: "Vendor" as const,
                        label: "Vendor",
                        nodeTitle: "Vendor",
                        nodeSubtitle: vendorDisplayName,
                        status: "synced" as const,
                        // The vendor record lives in the buying company's master (e.g. NSGB),
                        // but the vendor itself sits in another country. Show the vendor's
                        // physical country flag (countryCode) — keep the recording dataArea
                        // visible as a summary field so the context isn't lost.
                        countryCode: vendor.addressCountryRegionISOCode || undefined,
                        source: "d365" as const,
                        summaryFields: [
                            { label: "Account", value: vendor.vendorAccountNumber },
                            ...(vendor.addressCity ? [{ label: "City", value: vendor.addressCity }] : []),
                            ...(vendor.vendorGroupId ? [{ label: "Group", value: vendor.vendorGroupId }] : []),
                            ...(vendor.isIntercompany ? [{ label: "Intercompany", value: "Yes" }] : []),
                            ...(vendor.dataAreaId ? [{ label: "Recorded in", value: vendor.dataAreaId.toUpperCase() }] : []),
                        ],
                        isSelected: false,
                        onSelect: onNodeSelect,
                        vendor: {
                            vendorAccountNumber: vendor.vendorAccountNumber,
                            dataAreaId: vendor.dataAreaId,
                            vendorName: vendor.vendorName,
                            vendorKnownAsName: vendor.vendorKnownAsName,
                            vendorPartyNumber: vendor.vendorPartyNumber,
                            vendorGroupId: vendor.vendorGroupId,
                            addressCountryRegionId: vendor.addressCountryRegionId,
                            addressCountryRegionISOCode: vendor.addressCountryRegionISOCode,
                            addressCity: vendor.addressCity,
                            isIntercompany: vendor.isIntercompany,
                            intercompanyDataAreaIds: vendor.intercompanyDataAreaIds,
                        },
                    },
                } as ExplorerNode);
            }

            routeEdges.push({
                id: `e-${edgePrefix}vendor-${rpKey}`,
                source: sourceRpNodeId,
                target: vendorNodeId,
            });

            // For each manufacturing dataAreaId, render a cross-company RP node and recurse
            // its D365 Route + BOM chain. External vendors (no intercompany matches) leave
            // the Vendor node as a terminal leaf.
            for (const mfgDataAreaId of vendor.intercompanyDataAreaIds) {
                const crossRpKey = `${sourcing.itemNumber}|${mfgDataAreaId}`;
                const crossRpNodeId = `ReleasedProduct-${crossRpKey}`;

                routeEdges.push({
                    id: `e-${edgePrefix}vendorrp-${crossRpKey}`,
                    source: vendorNodeId,
                    target: crossRpNodeId,
                });

                if (visitedRPKeys.has(crossRpKey)) {
                    continue;
                }
                visitedRPKeys.add(crossRpKey);

                routeNodes.push({
                    id: crossRpNodeId,
                    type: "entityNode",
                    position: { x: 0, y: 0 },
                    data: {
                        entityType: "ReleasedProduct" as const,
                        label: "Released Product",
                        nodeTitle: "Released Product",
                        nodeSubtitle: sourcing.itemNumber,
                        status: "synced" as const,
                        dataAreaId: mfgDataAreaId,
                        source: "d365" as const,
                        summaryFields: [
                            { label: "Product Number", value: sourcing.itemNumber },
                        ],
                        isSelected: false,
                        onSelect: onNodeSelect,
                        d365Exists: true,
                    },
                } as ExplorerNode);

                buildD365RouteChain(crossRpKey, crossRpNodeId, mfgDataAreaId);
            }
            return true;
        }

        /** Create a Route → OpDetail → BomLines chain for a given RP key, appending to routeNodes/routeEdges */
        function buildRouteChain(rpKey: string, sourceNodeId: string, dataAreaId: string, edgePrefix: string) {
            const resolvedRoute = routeVersionMap.get(rpKey);
            if (!resolvedRoute) {
                return;
            }

            // Use the manufacturing dataArea from the resolved route (not the parent's). For
            // cross-company items the parent's dataAreaId differs from where the route lives —
            // the route, op-detail and BOM-list nodes should reflect the manufacturing company.
            const routeDataAreaId = resolvedRoute.dataAreaID || dataAreaId;
            const routeNodeId = `Routes-${rpKey}`;
            const routeInfo = routeDataMap.get(rpKey);
            const opsLoading = routeInfo?.isLoading ?? true;
            const opsError = routeInfo?.isError ?? false;
            const routeStatus = opsLoading ? "loading" : opsError ? "error" : "synced";

            routeNodes.push({
                id: routeNodeId,
                type: "routeOperationsListNode",
                position: { x: 0, y: 0 },
                data: {
                    entityType: "Route" as const,
                    label: "Route",
                    nodeTitle: "Route",
                    nodeSubtitle: resolvedRoute.routeId,
                    status: routeStatus as ExplorerNodeStatus,
                    dataAreaId: routeDataAreaId,
                    summaryFields: [],
                    routeId: resolvedRoute.routeId,
                    routeVersionSiteId: resolvedRoute.productionSiteId || undefined,
                    routeOperations: (routeInfo?.operations ?? [])
                        .map((op) => ({
                            operationNumber: op.operationNumber,
                            operationId: op.operationId,
                            operationPriority: op.operationPriority,
                            nextOperationNumber: op.nextRouteOperationNumber,
                            isExpanded: expandedOperations.includes(`${rpKey}|${op.operationNumber}`),
                        })),
                    isSelected: false,
                    onSelect: onNodeSelect,
                    onOperationSelect: (operationNumber: number) => onOperationSelect(rpKey, operationNumber),
                    d365Exists: routeExistsMap.get(rpKey) ?? null,
                },
            } as ExplorerNode);

            routeEdges.push({
                id: `e-${edgePrefix}route-${rpKey}`,
                source: sourceNodeId,
                target: routeNodeId,
            });

            // Expanded operation detail nodes + BOM lines
            const opProperties = routeOpPropertyMap.get(rpKey) ?? [];
            const opRequirements = routeOpRequirementMap.get(rpKey) ?? [];
            const allBomLines = bomLineMap.get(rpKey) ?? [];

            for (const opKey of expandedOperations) {
                if (!opKey.startsWith(rpKey + "|")) {
                    continue;
                }
                const operationNumber = parseInt(opKey.split("|")[2], 10);
                if (isNaN(operationNumber)) {
                    continue;
                }

                const op = (routeInfo?.operations ?? []).find((o) => o.operationNumber === operationNumber);
                if (!op) {
                    continue;
                }

                const prop = opProperties.find(
                    (p) => p.operationNumber === operationNumber && p.operationId === op.operationId,
                );
                const reqs = opRequirements.filter(
                    (r) => r.operationNumber === operationNumber && r.routeOperationId === op.operationId,
                );

                const opDetailNodeId = `OpDetail-${opKey}`;

                routeNodes.push({
                    id: opDetailNodeId,
                    type: "operationDetailNode",
                    position: { x: 0, y: 0 },
                    data: {
                        entityType: "RouteOperationDetail" as const,
                        label: `Operation ${op.operationId}`,
                        nodeTitle: "Operation",
                        nodeSubtitle: splitPascalCase(op.operationId),
                        status: prop ? "synced" : "loading",
                        dataAreaId: routeDataAreaId,
                        summaryFields: [],
                        isSelected: false,
                        onSelect: onNodeSelect,
                        operationProperty: prop
                            ? {
                                operationId: prop.operationId,
                                operationNumber: prop.operationNumber,
                                operationPriority: prop.operationPriority,
                                routeGroupId: prop.routeGroupId,
                                routeType: prop.routeType,
                                processTime: prop.processTime,
                                setupTime: prop.setupTime,
                                queueTimeBefore: prop.queueTimeBefore,
                                queueTimeAfter: prop.queueTimeAfter,
                                transitTime: prop.transitTime,
                                loadPercentage: prop.loadPercentage,
                                resourceQuantity: prop.resourceQuantity,
                                processQuantity: prop.processQuantity,
                                costingOperationResourceId: prop.costingOperationResourceId,
                                processCostCategoryId: prop.processCostCategoryId,
                                setupCostCategoryId: prop.setupCostCategoryId,
                                quantityCostCategoryId: prop.quantityCostCategoryId,
                                productionSiteId: prop.productionSiteId,
                                itemNumber: prop.itemNumber,
                            }
                            : undefined,
                        operationRequirements: reqs.map((r) => ({
                            operationNumber: r.operationNumber,
                            routeOperationId: r.routeOperationId,
                            requiredOperationsResourceGroupId: r.requiredOperationsResourceGroupId,
                            requiredOperationsResourceId: r.requiredOperationsResourceId,
                            requiredCapabilityName: r.requiredCapabilityName,
                            willJobSchedulingUseRequirement: r.willJobSchedulingUseRequirement,
                            willOperationSchedulingUseRequirement: r.willOperationSchedulingUseRequirement,
                        })),
                    },
                } as ExplorerNode);

                routeEdges.push({
                    id: `e-${edgePrefix}opdetail-${opKey}`,
                    source: routeNodeId,
                    target: opDetailNodeId,
                });

                // BOM lines for this operation
                const opBomLines = allBomLines.filter(
                    (bl) => bl.routeOperationNumber === operationNumber,
                );

                if (opBomLines.length > 0) {
                    const bomNodeId = `BomLines-${opKey}`;

                    routeNodes.push({
                        id: bomNodeId,
                        type: "bomLinesListNode",
                        position: { x: 0, y: 0 },
                        data: {
                            entityType: "BomLines" as const,
                            label: "BOM Lines",
                            nodeTitle: "BOM",
                            nodeSubtitle: splitPascalCase(op.operationId),
                            status: "synced" as const,
                            dataAreaId: routeDataAreaId,
                            summaryFields: [],
                            isSelected: false,
                            onSelect: onNodeSelect,
                            onBomLineSelect,
                            bomLines: opBomLines.map((bl) => ({
                                itemNumber: bl.itemNumber,
                                dataAreaId: bl.dataAreaID,
                                quantity: bl.quantity,
                                productUnitSymbol: bl.productUnitSymbol,
                                lineType: bl.lineType,
                                flushingPrinciple: bl.flushingPrinciple,
                                consumptionWarehouseId: bl.consumptionWarehouseId,
                                vendorAccountNumber: bl.vendorAccountNumber,
                                routeOperationNumber: bl.routeOperationNumber,
                                stdSailItem: bl.stdSailItem,
                                isExpanded: expandedBomLines.includes(`${bl.itemNumber}|${bl.dataAreaID}`),
                            })),
                        },
                    } as ExplorerNode);

                    routeEdges.push({
                        id: `e-${edgePrefix}bom-${opKey}`,
                        source: opDetailNodeId,
                        target: bomNodeId,
                    });

                    // Recurse into expanded BOM lines
                    expandBomLineItems(opBomLines, bomNodeId, opKey);
                }
            }
        }

        /** Create a D365-sourced Route → OpDetail → BomLines chain for a given RP key */
        function buildD365RouteChain(rpKey: string, sourceNodeId: string, dataAreaId: string) {
            const rv = d365RouteVersionMap.get(rpKey);
            if (!rv) {
                return;
            }

            // Display the manufacturing dataArea on the Route / OpDetail / BomLines nodes —
            // for cross-company purchased phantoms (NSG1 buys from NSSL), the parent's
            // dataAreaId is the purchase company but the route lives in the manufacturing
            // company. Fall back to the parent's dataAreaId only if the response lacks it.
            const routeDataAreaId = rv.dataAreaId || dataAreaId;
            const routeNodeId = `Routes-${rpKey}`;
            const routeInfo = d365RouteOperationsMap.get(rpKey);
            const opsLoading = routeInfo?.isLoading ?? true;
            const opsError = routeInfo?.isError ?? false;
            const routeStatus = opsLoading ? "loading" : opsError ? "error" : "synced";

            routeNodes.push({
                id: routeNodeId,
                type: "routeOperationsListNode",
                position: { x: 0, y: 0 },
                data: {
                    entityType: "Route" as const,
                    label: "Route",
                    nodeTitle: "Route",
                    nodeSubtitle: rv.routeId,
                    status: routeStatus as ExplorerNodeStatus,
                    dataAreaId: routeDataAreaId,
                    source: "d365" as const,
                    summaryFields: [],
                    routeId: rv.routeId,
                    routeVersionSiteId: rv.productionSiteId || undefined,
                    routeOperations: (routeInfo?.operations ?? []).map((op) => ({
                        operationNumber: op.operationNumber,
                        operationId: op.operationId,
                        operationPriority: op.operationPriority,
                        nextOperationNumber: op.nextRouteOperationNumber,
                        isExpanded: expandedOperations.includes(`${rpKey}|${op.operationNumber}`),
                    })),
                    isSelected: false,
                    onSelect: onNodeSelect,
                    onOperationSelect: (operationNumber: number) => onOperationSelect(rpKey, operationNumber),
                    d365Exists: true,
                },
            } as ExplorerNode);

            routeEdges.push({
                id: `e-d365route-${rpKey}`,
                source: sourceNodeId,
                target: routeNodeId,
            });

            // Expanded operation detail nodes + BOM lines
            const opPropertiesInfo = d365OpPropertyMap.get(rpKey);
            const opProperties = opPropertiesInfo?.data ?? EMPTY_OP_PROPERTIES_D365;
            const opPropertiesLoading = opPropertiesInfo?.isLoading ?? false;
            const opRequirements = d365OpRequirementMap.get(rpKey) ?? EMPTY_OP_REQUIREMENTS_D365;
            const allBomLines = d365BomLineMap.get(rpKey) ?? EMPTY_BOM_LINES_D365;

            // D365 BOM lines may be assigned to a specific operation (RouteOperationNumber > 0)
            // or unassigned (RouteOperationNumber === 0, the common case for phantom BOMs).
            // Unassigned lines apply to the route as a whole and hang off the Route node;
            // assigned lines hang off their specific operation node.
            const unassignedBomLines = allBomLines.filter((bl) => !bl.routeOperationNumber);
            if (unassignedBomLines.length > 0) {
                const routeBomNodeId = `BomLines-d365-route-${rpKey}`;
                const hasAssignedLines = allBomLines.some((bl) => bl.routeOperationNumber > 0);
                const noteText = hasAssignedLines
                    ? "These BOM lines have no operation assignment in D365 — assigned lines appear under their operation."
                    : "These BOM lines have no operation assignment in D365.";
                routeNodes.push({
                    id: routeBomNodeId,
                    type: "bomLinesListNode",
                    position: { x: 0, y: 0 },
                    data: {
                        entityType: "BomLines" as const,
                        label: "BOM Lines",
                        nodeTitle: "BOM",
                        nodeSubtitle: rv.routeId,
                        status: "synced" as const,
                        dataAreaId: routeDataAreaId,
                        source: "d365" as const,
                        summaryFields: [],
                        bomNote: noteText,
                        isSelected: false,
                        onSelect: onNodeSelect,
                        onBomLineSelect,
                        bomLines: unassignedBomLines.map((bl) => ({
                            itemNumber: bl.itemNumber,
                            dataAreaId: bl.dataAreaId,
                            quantity: bl.quantity,
                            productUnitSymbol: bl.productUnitSymbol,
                            lineType: bl.lineType,
                            flushingPrinciple: bl.flushingPrinciple,
                            consumptionWarehouseId: bl.consumptionWarehouseId,
                            vendorAccountNumber: bl.vendorAccountNumber,
                            routeOperationNumber: bl.routeOperationNumber,
                            stdSailItem: bl.lineType === "Phantom",
                            isExpanded: expandedBomLines.includes(`${bl.itemNumber}|${bl.dataAreaId}`),
                        })),
                    },
                } as ExplorerNode);
                routeEdges.push({
                    id: `e-d365bom-route-${rpKey}`,
                    source: routeNodeId,
                    target: routeBomNodeId,
                });
                expandD365BomLineItems(unassignedBomLines, routeBomNodeId, rpKey, dataAreaId);
            }

            for (const opKey of expandedOperations) {
                if (!opKey.startsWith(rpKey + "|")) {
                    continue;
                }
                const operationNumber = parseInt(opKey.split("|")[2], 10);
                if (isNaN(operationNumber)) {
                    continue;
                }

                const op = (routeInfo?.operations ?? []).find((o) => o.operationNumber === operationNumber);
                if (!op) {
                    continue;
                }

                // Match by operationId only: D365's Shea_RouteOperationProperties /
                // RouteOperationPropertiesResourceRequirements don't expose OperationNumber on
                // every row (it deserializes as 0), but operationId is reliably populated and
                // unique per route operation.
                const prop = opProperties.find((p) => p.operationId === op.operationId);
                const reqs = opRequirements.filter((r) => r.routeOperationId === op.operationId);

                const opDetailNodeId = `OpDetail-${opKey}`;

                // Only show loading skeleton while the property query is still in flight.
                // Once fetched, render whatever we got (could be empty / no match).
                const opDetailStatus: ExplorerNodeStatus = opPropertiesLoading
                    ? "loading"
                    : "synced";

                routeNodes.push({
                    id: opDetailNodeId,
                    type: "operationDetailNode",
                    position: { x: 0, y: 0 },
                    data: {
                        entityType: "RouteOperationDetail" as const,
                        label: `Operation ${op.operationId}`,
                        nodeTitle: "Operation",
                        nodeSubtitle: splitPascalCase(op.operationId),
                        status: opDetailStatus,
                        dataAreaId: routeDataAreaId,
                        source: "d365" as const,
                        summaryFields: [],
                        isSelected: false,
                        onSelect: onNodeSelect,
                        operationProperty: prop
                            ? {
                                operationId: prop.operationId,
                                operationNumber: prop.operationNumber,
                                operationPriority: prop.operationPriority,
                                routeGroupId: prop.routeGroupId,
                                routeType: prop.routeType,
                                processTime: prop.processTime,
                                setupTime: prop.setupTime,
                                queueTimeBefore: prop.queueTimeBefore,
                                queueTimeAfter: prop.queueTimeAfter,
                                transitTime: prop.transitTime,
                                loadPercentage: prop.loadPercentage,
                                resourceQuantity: prop.resourceQuantity,
                                processQuantity: prop.processQuantity,
                                costingOperationResourceId: prop.costingOperationResourceId,
                                processCostCategoryId: prop.processCostCategoryId,
                                setupCostCategoryId: prop.setupCostCategoryId,
                                quantityCostCategoryId: prop.quantityCostCategoryId,
                                productionSiteId: prop.productionSiteId,
                                itemNumber: prop.itemNumber,
                            }
                            : undefined,
                        operationRequirements: reqs.map((r) => ({
                            operationNumber: r.operationNumber,
                            routeOperationId: r.routeOperationId,
                            requiredOperationsResourceGroupId: r.requiredOperationsResourceGroupId,
                            requiredOperationsResourceId: r.requiredOperationsResourceId,
                            requiredCapabilityName: r.requiredCapabilityName,
                            willJobSchedulingUseRequirement: r.willJobSchedulingUseRequirement,
                            willOperationSchedulingUseRequirement: r.willOperationSchedulingUseRequirement,
                        })),
                    },
                } as ExplorerNode);

                routeEdges.push({
                    id: `e-d365opdetail-${opKey}`,
                    source: routeNodeId,
                    target: opDetailNodeId,
                });

                // BOM lines assigned to this specific operation (RouteOperationNumber > 0).
                // Unassigned lines (RouteOperationNumber === 0) are attached to the Route node
                // above, not duplicated under each operation.
                const opBomLines = allBomLines.filter(
                    (bl) => bl.routeOperationNumber > 0 && bl.routeOperationNumber === operationNumber,
                );

                if (opBomLines.length > 0) {
                    const bomNodeId = `BomLines-d365-${opKey}`;

                    routeNodes.push({
                        id: bomNodeId,
                        type: "bomLinesListNode",
                        position: { x: 0, y: 0 },
                        data: {
                            entityType: "BomLines" as const,
                            label: "BOM Lines",
                            nodeTitle: "BOM",
                            nodeSubtitle: splitPascalCase(op.operationId),
                            status: "synced" as const,
                            dataAreaId: routeDataAreaId,
                            source: "d365" as const,
                            summaryFields: [],
                            isSelected: false,
                            onSelect: onNodeSelect,
                            onBomLineSelect,
                            bomLines: opBomLines.map((bl) => ({
                                itemNumber: bl.itemNumber,
                                dataAreaId: bl.dataAreaId,
                                quantity: bl.quantity,
                                productUnitSymbol: bl.productUnitSymbol,
                                lineType: bl.lineType,
                                flushingPrinciple: bl.flushingPrinciple,
                                consumptionWarehouseId: bl.consumptionWarehouseId,
                                vendorAccountNumber: bl.vendorAccountNumber,
                                routeOperationNumber: bl.routeOperationNumber,
                                stdSailItem: bl.lineType === "Phantom",
                                isExpanded: expandedBomLines.includes(`${bl.itemNumber}|${bl.dataAreaId}`),
                            })),
                        },
                    } as ExplorerNode);

                    routeEdges.push({
                        id: `e-d365bom-${opKey}`,
                        source: opDetailNodeId,
                        target: bomNodeId,
                    });

                    expandD365BomLineItems(opBomLines, bomNodeId, opKey, dataAreaId);
                }
            }
        }

        /** Recursively expand D365 BOM line items into RP → Route → Op → BOM chains */
        function expandD365BomLineItems(
            bomLines: IBomLineD365[],
            parentBomNodeId: string,
            parentOpKey: string,
            _parentDataAreaId: string,
        ) {
            for (const bl of bomLines) {
                const childRpKey = `${bl.itemNumber}|${bl.dataAreaId}`;
                if (!expandedBomLines.includes(childRpKey)) {
                    continue;
                }

                const rpNodeId = `ReleasedProduct-${childRpKey}`;

                // Always add edge from BOM node → child RP node
                routeEdges.push({
                    id: `e-d365bomrp-${parentOpKey}-${bl.itemNumber}`,
                    source: parentBomNodeId,
                    target: rpNodeId,
                });

                // Skip duplicate node creation
                if (visitedRPKeys.has(childRpKey) || expandedRPNodes.some((n) => n.id === rpNodeId)) {
                    continue;
                }
                visitedRPKeys.add(childRpKey);

                // Create the child RP node — D365-sourced
                routeNodes.push({
                    id: rpNodeId,
                    type: "entityNode",
                    position: { x: 0, y: 0 },
                    data: {
                        entityType: "ReleasedProduct" as const,
                        label: "Released Product",
                        nodeTitle: "Released Product",
                        nodeSubtitle: bl.itemNumber,
                        status: "synced",
                        dataAreaId: bl.dataAreaId,
                        source: "d365" as const,
                        summaryFields: [
                            { label: "Product Number", value: bl.itemNumber },
                        ],
                        discrepancyCount: 0,
                        totalFields: 0,
                        isSelected: false,
                        onSelect: onNodeSelect,
                        d365Exists: true,
                    },
                } as ExplorerNode);

                // Sourcing-driven Vendor chain takes precedence — when the local-company RP
                // is Purch, we hop to the manufacturer via a Vendor node instead of recursing
                // into the local stub route.
                if (tryInsertVendorChain(childRpKey, rpNodeId, "d365bom")) {
                    continue;
                }

                // Recurse — only if this child BOM line is a phantom in D365
                if (bl.lineType === "Phantom") {
                    buildD365RouteChain(childRpKey, rpNodeId, bl.dataAreaId);
                }
            }
        }

        /** Recursively expand BOM line items into RP → Route → Op → BOM chains */
        function expandBomLineItems(
            bomLines: IBomLineCS[],
            parentBomNodeId: string,
            parentOpKey: string,
        ) {
            for (const bl of bomLines) {
                const rpKey = `${bl.itemNumber}|${bl.dataAreaID}`;
                if (!expandedBomLines.includes(rpKey)) {
                    continue;
                }

                const rpNodeId = `ReleasedProduct-${rpKey}`;

                // Always add edge from BOM node → RP node
                routeEdges.push({
                    id: `e-bomrp-${parentOpKey}-${bl.itemNumber}`,
                    source: parentBomNodeId,
                    target: rpNodeId,
                });

                // Avoid duplicate nodes (same RP from multiple BOM lines or from RP list expansion)
                if (visitedRPKeys.has(rpKey) || expandedRPNodes.some((n) => n.id === rpNodeId)) {
                    continue;
                }
                visitedRPKeys.add(rpKey);

                // Create RP entity node — use D365 existence check (not comparison)
                const d365Exists = bomRPExistsMap.get(rpKey) ?? null;
                const bomRPStatus: ExplorerNodeStatus = d365Exists === null
                    ? "loading"
                    : d365Exists ? "synced" : "missing";

                routeNodes.push({
                    id: rpNodeId,
                    type: "entityNode",
                    position: { x: 0, y: 0 },
                    data: {
                        entityType: "ReleasedProduct" as const,
                        label: "Released Product",
                        nodeTitle: "Released Product",
                        nodeSubtitle: bl.itemNumber,
                        status: bomRPStatus,
                        dataAreaId: bl.dataAreaID,
                        summaryFields: [
                            { label: "Product Number", value: bl.itemNumber },
                        ],
                        discrepancyCount: 0,
                        totalFields: 0,
                        isSelected: false,
                        onSelect: onNodeSelect,
                        d365Exists,
                    },
                } as ExplorerNode);

                // If sourcing reports DefaultOrderType=Purch, insert a Vendor (and any
                // cross-company manufacturing RPs hanging off it) instead of recursing into
                // the local-company route — the BOM/Route in the purchasing company is just a
                // stub. tryInsertVendorChain returns true when it took over the chain.
                if (tryInsertVendorChain(rpKey, rpNodeId, "bom")) {
                    continue;
                }

                // Decide which chain to build for this BOM-sourced RP
                const csRouteVersion = routeVersionMap.get(rpKey);
                if (csRouteVersion) {
                    // CS has the route — recurse on CS data (existing behavior)
                    buildRouteChain(rpKey, rpNodeId, bl.dataAreaID, "bom");
                } else if (csRouteVersion === null && bl.stdSailItem) {
                    // CS resolved null AND BOM line is phantom — try D365 fallback
                    buildD365RouteChain(rpKey, rpNodeId, bl.dataAreaID);
                }
                // Else: still loading (csRouteVersion === undefined), or non-phantom non-CS-routed item.
            }
        }

        for (const rpKey of expandedReleasedProducts) {
            const [itemNumber, dataAreaId] = rpKey.split("|");
            if (!itemNumber || !dataAreaId) {
                continue;
            }

            // Find the parent ReleasedProducts list that contains this item
            const parentListNode = releasedProductNodes.find((n) => {
                const items = n.data.releasedProducts ?? [];
                return items.some((i) => i.itemNumber === itemNumber && i.dataAreaId === dataAreaId);
            });

            if (!parentListNode) {
                continue;
            }

            const rpItem = (parentListNode.data.releasedProducts ?? [])
                .find((i) => i.itemNumber === itemNumber && i.dataAreaId === dataAreaId);

            const mapKey = `${itemNumber}|${dataAreaId}`;
            const rpQuery = releasedProductMap.get(mapKey);
            const rpStatus = rpQuery
                ? computeStatus(rpQuery.data, rpQuery.isLoading, rpQuery.isError, rpQuery.isFetched)
                : { status: "na" as const, discrepancyCount: 0, totalFields: 0 };

            // Resolve productNumber from the parent list node ID (e.g. "ReleasedProducts-ABC123")
            const productNumber = parentListNode.id.replace("ReleasedProducts-", "");

            const nodeId = `ReleasedProduct-${rpKey}`;
            const rpCompData = rpQuery?.data;
            const rpProductionType = rpCompData?.find((f) => f.sourcePropertyName === "ProductionType")?.sourceValue ?? null;
            const rpDefaultOrderType = rpCompData?.find((f) => f.sourcePropertyName === "DefaultOrderType")?.sourceValue ?? null;

            expandedRPNodes.push({
                id: nodeId,
                type: "entityNode",
                position: { x: 0, y: 0 },
                data: {
                    entityType: "ReleasedProduct" as const,
                    label: `Released Product`,
                    nodeTitle: "Released Product",
                    nodeSubtitle: itemNumber,
                    status: rpStatus.status,
                    dataAreaId,
                    summaryFields: [
                        { label: "Production Type", value: rpProductionType as string | null },
                    ],
                    discrepancyCount: rpStatus.discrepancyCount,
                    totalFields: rpStatus.totalFields,
                    isSelected: false,
                    onSelect: onNodeSelect,
                    d365Exists: rpQuery?.isFetched
                        ? (rpQuery.data !== undefined && !rpQuery.isError)
                        : null,
                    make: rpItem?.make,
                },
            } as ExplorerNode);

            expandedRPEdges.push({
                id: `e-rpexp-${rpKey}`,
                source: parentListNode.id,
                target: nodeId,
            });

            buildRouteChain(rpKey, nodeId, dataAreaId, "");
        }

        // FM Route nodes — one per expanded line with a valid mItemID
        const fmRouteNodes: ExplorerNode[] = [];
        const fmRouteEdges: ExplorerEdge[] = [];

        for (const lineNum of expandedLineNumbers) {
            const fmInfo = fmRouteMap.get(lineNum);
            if (!fmInfo) {
                continue;
            }

            const fmLine = linesData?.find((l) => l.lineNumber === lineNum);
            const fmNodeId = `FMRoute-${lineNum}`;
            const fmStatus: ExplorerNodeStatus = fmInfo.isLoading
                ? "loading"
                : fmInfo.isError
                    ? "error"
                    : "na";

            fmRouteNodes.push({
                id: fmNodeId,
                type: "fmRouteNode",
                zIndex: -1,
                position: { x: 0, y: 0 },
                data: {
                    entityType: "FMRoute" as const,
                    label: "FM Manufacturing Route",
                    nodeTitle: "FM Route",
                    nodeSubtitle: fmLine?.externalItemNumber || fmLine?.itemNumber || `Line ${lineNum}`,
                    status: fmStatus,
                    summaryFields: [],
                    isSelected: false,
                    onSelect: onNodeSelect,
                    fmCurrentStagePercentage: fmInfo.data?.currentStagePercentage,
                    fmStatusForOutput: fmInfo.data?.statusForOutput,
                    fmSiteSteps: fmInfo.data?.siteSteps?.map((step) => ({
                        siteTypeId: step.siteTypeId,
                        siteTypeName: step.siteTypeName,
                        siteName: step.siteName,
                        divisionName: step.divisionName,
                        siteId: step.siteId,
                        divisionId: step.divisionId,
                        countryCode: step.countryCode,
                        startDate: step.startDate,
                        shipDate: step.shipDate,
                    })) ?? [],
                },
            } as ExplorerNode);

            fmRouteEdges.push({
                id: `e-fmroute-${lineNum}`,
                source: `SalesOrderLineDetail-${lineNum}`,
                sourceHandle: "bottom",
                target: fmNodeId,
                targetHandle: "top",
            });
        }

        const fullIdPrefixes = ["SalesOrderLineDetail-", "ReleasedProduct-", "Product-", "Routes-", "OpDetail-", "BomLines-", "FMRoute-", "Vendor-"];
        const allNodes: ExplorerNode[] = [
            soHeaderNode, headerChargeNode, customerPaymentsNode, customerNode,
            ...(hasValidVatNum ? [vatNumNode] : []),
            linesNode, ...lineDetailNodes, ...productNodes, ...releasedProductNodes,
            ...expandedRPNodes, ...routeNodes, ...fmRouteNodes,
        ].map((n) => ({
            ...n,
            dragHandle: ".node-drag-handle",
            data: {
                ...n.data,
                selectId: fullIdPrefixes.some((p) => n.id.startsWith(p)) ? n.id : n.data.entityType,
            },
        }));
        const allEdges: ExplorerEdge[] = [
            { id: "e-header-charge", source: "SalesOrderHeaderCharge", target: "SalesOrderHeader" },
            { id: "e-customer-payments", source: "CustomerPayments", target: "SalesOrderHeader" },
            { id: "e-customer", source: "Customer", target: "SalesOrderHeader" },
            ...(hasValidVatNum ? [{ id: "e-vatnum", source: "VatNum", target: "SalesOrderHeader" }] : []),
            { id: "e-lines", source: "SalesOrderHeader", target: "SalesOrderLines" },
            ...lineDetailEdges,
            ...productEdges,
            ...releasedProductEdges,
            ...expandedRPEdges,
            ...routeEdges,
            ...fmRouteEdges,
        ];

        return { nodes: allNodes, edges: allEdges };
    }, [
        init,
        initLoading,
        header,
        salesOrderStatus,
        orderID,
        headerComparison.data,
        headerComparison.isLoading,
        headerComparison.isError,
        headerComparison.isFetched,
        headerChargeComparison.data,
        headerChargeComparison.isLoading,
        headerChargeComparison.isError,
        headerChargeComparison.isFetched,
        customerPaymentsQuery.data,
        customerPaymentsQuery.isLoading,
        customerPaymentsQuery.isError,
        customerComparison.data,
        customerComparison.isLoading,
        customerComparison.isError,
        customerComparison.isFetched,
        vatNumComparison.data,
        vatNumComparison.isLoading,
        vatNumComparison.isError,
        vatNumComparison.isFetched,
        linesData,
        linesLoading,
        existenceMap,
        designMap,
        itemsByDesignMap,
        productMakeMap,
        releasedProductMap,
        productMap,
        buyProductMap,
        buyRPMap,
        hasValidVatNum,
        expandedLineNumbers,
        expandedReleasedProducts,
        routeVersionMap,
        routeDataMap,
        routeExistsMap,
        routeOpPropertyMap,
        routeOpRequirementMap,
        bomLineMap,
        bomRPExistsMap,
        sourcingMap,
        d365RouteVersionMap,
        d365RouteOperationsMap,
        d365OpPropertyMap,
        d365OpRequirementMap,
        d365BomLineMap,
        fmRouteMap,
        expandedOperations,
        expandedBomLines,
        onNodeSelect,
        onLineSelect,
        onReleasedProductSelect,
        onOperationSelect,
        onBomLineSelect,
    ]);

    // Unified node state — ReactFlow owns positions, dimensions, selection via applyNodeChanges
    const [nodesState, setNodesState] = useState<ExplorerNode[]>([]);
    const prevStructureKeyRef = useRef<string>("");

    // Structure fingerprint — node/edge IDs plus the dynamic-height inputs of nodes whose
    // dagre height isn't fixed (CustomerPayments grows with payment count). Keeping this
    // in the key forces a re-layout when payments load asynchronously, so spacing stays
    // even between Customer / CustomerPayments / HeaderCharges.
    const structureKey = useMemo(() => {
        const nk = rawGraph.nodes.map((n) => n.id).sort().join(",");
        const ek = rawGraph.edges.map((e) => e.id).sort().join(",");
        const paymentsNode = rawGraph.nodes.find((n) => n.id === "CustomerPayments");
        const paymentCount = paymentsNode?.data.payments?.length ?? 0;
        return `${nk}|${ek}|p${paymentCount}`;
    }, [rawGraph.nodes, rawGraph.edges]);

    // Re-run dagre layout only when the graph structure changes (nodes added/removed)
    // Preserves drag positions for nodes that already existed
    useEffect(() => {
        if (structureKey === prevStructureKeyRef.current) {
            return;
        }
        prevStructureKeyRef.current = structureKey;
        const { nodes: layouted } = applyDagreLayout(rawGraph.nodes, rawGraph.edges);
        setNodesState((prev) => {
            // On structure change, accept dagre's fresh positions for all nodes — preserving
            // old positions wholesale here would freeze the layout when a node's dagre height
            // changes (e.g., CustomerPayments growing as payments load) and break spacing.
            // Only preserve ReactFlow's measured dimensions to avoid a flicker.
            const prevMap = new Map(prev.map((n) => [n.id, n]));
            return layouted.map((n) => ({
                ...n,
                measured: prevMap.get(n.id)?.measured ?? n.measured,
            }));
        });
    }, [structureKey, rawGraph.nodes, rawGraph.edges]);

    // Merge latest data from rawGraph onto nodesState (preserves positions, dimensions, etc.)
    // Also apply isSelected here instead of inside rawGraph to avoid recomputing the entire
    // graph on every selection change.
    const nodes = useMemo(() => {
        const applySelection = (n: ExplorerNode): ExplorerNode => ({
            ...n,
            data: { ...n.data, isSelected: selectedNodeId === n.id },
        });
        if (nodesState.length === 0) {
            // First render before effect fires — compute layout inline
            const result = applyDagreLayout(rawGraph.nodes, rawGraph.edges);
            return result.nodes.map(applySelection);
        }
        const stateMap = new Map(nodesState.map((n) => [n.id, n]));
        return rawGraph.nodes.map((n) => {
            const merged = stateMap.has(n.id)
                ? { ...stateMap.get(n.id)!, data: n.data, dragHandle: n.dragHandle }
                : n;
            return applySelection(merged);
        });
    }, [rawGraph.nodes, nodesState, selectedNodeId]);

    const edges = rawGraph.edges;

    // Apply ALL ReactFlow changes (positions, dimensions, selection) to nodesState
    const onNodesChange = useCallback((changes: NodeChange<ExplorerNode>[]) => {
        setNodesState((prev) => applyNodeChanges(changes, prev));
    }, []);

    const comparisonDataMap = useMemo(
        () => {
            const map: Record<string, IComparisonInfo[] | undefined> = {
                SalesOrderHeader: headerComparison.data,
                SalesOrderHeaderCharge: headerChargeComparison.data,
                Customer: customerComparison.data,
                ...(hasValidVatNum ? { VatNum: vatNumComparison.data } : {}),
            };
            // Add per-item released product comparisons (keyed by "itemNumber|dataAreaId")
            for (const [rpKey, rpQuery] of releasedProductMap) {
                map[`ReleasedProduct-${rpKey}`] = rpQuery.data;
            }
            // Add per-productNumber product comparisons
            for (const [productNumber, pQuery] of productMap) {
                map[`Product-${productNumber}`] = pQuery.data;
            }
            return map;
        },
        [headerComparison.data, headerChargeComparison.data, customerComparison.data, hasValidVatNum, vatNumComparison.data, releasedProductMap, productMap],
    );

    return {
        nodes,
        edges,
        onNodesChange,
        isInitLoading: initLoading,
        comparisonDataMap,
        existenceData,
        routeComparisonMap,
        bomRPComparisonMap,
        bomLineMap,
        buyProductMap,
        buyRPMap,
    };
}
